import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, View, Views, SlotInfo } from 'react-big-calendar';
import type { ToolbarProps } from 'react-big-calendar';
import moment from 'moment';
import 'moment-timezone';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { Calendar as CalendarIcon, Clock, Plus, X, Trash2, Eye, List, CheckCircle, AlertTriangle, MousePointerClick, CheckSquare, DollarSign, Package, XCircle, Edit, ChevronLeft, ChevronRight, RotateCcw, FileText } from 'lucide-react';
import PatientSearchSelect from '../../components/common/PatientSearchSelect';
import AttendanceCertificateModal from '../../components/admin/AttendanceCertificateModal';
import ActionButton from '../../components/ui/ActionButton';
import {
  AppointmentStatusService,
  FinancialOriginKind,
  FinancialOriginOption,
  FinancialOriginPreview,
  ReopenNoEvaluatedPreviewResult,
  type AbsenceDecisionPreview,
} from '../../services/AppointmentStatusService';
import { AbsenceDecisionModal } from '../../components/common/AbsenceDecisionModal';
import { isClientAccessActive } from '../../utils/clientAccess';
import {
  AppointmentLocation,
  describeAppointmentLocationIssue,
  formatAppointmentLocation,
  normalizeAppointmentLocation,
} from '../../utils/appointmentLocation';
import { IntegrationService } from '../../services/IntegrationService';
import { useClinicProfile } from '../../context/ClinicProfileContext';
import { ClinicTimeZoneService } from '../../services/ClinicTimeZoneService';
import { UserService } from '../../services/UserService';
import {
  AttendanceCertificateFormData,
  AttendanceCertificatePDFService,
} from '../../services/AttendanceCertificatePDFService';
import { ClientReportSettingsService } from '../../features/assessments-v2/services/ClientReportSettingsService';
import { DocumentVerificationService } from '../../services/DocumentVerificationService';
import clinicInfo from '../../config/clinicInfo';
import { createAgendaTimeZoneLocalizer } from '../../utils/agendaTimeZoneLocalizer';
import { normalizeAgendaColor } from '../../utils/agendaColor';
import {
  APPOINTMENT_BATCH_LIMITS,
  buildAppointmentOccurrences,
  buildSpecificAppointmentRanges,
  buildWeeklyAppointmentRanges,
  createAppointmentBatchId,
  matchesCompleteAppointmentBatch,
} from '../../utils/appointmentBatch';
import type {
  AppointmentCreateMode,
  AppointmentDateRange,
  SpecificAppointmentSlot,
  WeeklyAppointmentSlot,
} from '../../utils/appointmentBatch';
import { buildTenantPath } from '../../utils/tenantSlug';
import { buildSignedDocumentMetadata } from '../../utils/documentVerification';
import {
  buildMedicalRecordAuthorSnapshot,
  fetchMedicalRecordAuthors,
} from '../../utils/medicalRecordAuthor';
import {
  addCalendarDaysInTimeZone,
  DEFAULT_TIME_ZONE,
  formatInTimeZone,
  getTimeZoneLabel,
  isValidTimeZone,
  parseDateAndTimeInTimeZone,
  parseDateTimeLocalInTimeZone,
  toDateTimeLocalInTimeZone,
} from '../../utils/timeZone';


// Configurar moment para português
moment.locale('pt-br');
moment.updateLocale('pt-br', {
  week: {
    dow: 1,
    doy: 4,
  },
});
interface AgendaEvent {
  id: string;
  title: string;          // subject
  start: Date;
  end: Date;
  doctorId?: string;
  userId?: string;
  calendar_name?: string; // nome do calendário / profissional
  location?: AppointmentLocation;
  rawLocation?: string | null;
  locationIssue?: string | null;
  patientId?: string;     // ID do paciente em cadastro_de_pacientes
  status?: 'scheduled' | 'completed' | 'no_show' | 'cancelled' | 'no_billed' | 'no_evaluated';
  evolutionId?: string;   // ID da evolução vinculada
  invoiceId?: string;     // ID da fatura vinculada
  financialInvoiceId?: string; // ID da fatura do novo financeiro
  isBilled?: boolean;     // Se já foi faturado
  financialAppointmentId?: string; // ID do agendamento no perfil financeiro
  serviceId?: string;
  serviceName?: string;
}

type AgendaEventUiStatus = 'scheduled' | 'completed' | 'no_show' | 'cancelled' | 'no_billed' | 'no_evaluated';

interface AgendaAppointmentRow {
  appointmentId: string;
  appointmentTitle?: string | null;
  appointmentStartDate: string;
  appointmentEndDate: string;
  doctorId?: string | null;
  userId?: string | null;
  appointmentLocation?: string | null;
  patientId?: string | null;
  appointmentStatus?: string | null;
  appointmentEvolutionId?: string | null;
  invoiceId?: string | null;
  financialInvoiceId?: string | null;
  appointmentIsBilled?: boolean | null;
  financialAppointmentId?: string | null;
  appointmentServiceId?: string | null;
  appointmentServiceNameSnapshot?: string | null;
  users?: {
    userAssignedCalendarName?: string | null;
    userDisplayName?: string | null;
  } | null;
}

interface CalendarColorEntry {
  key: string;
  label: string;
  sortLabel: string;
  accentColor: string | null;
}

interface CalendarLegendItem {
  id: string;
  label: string;
  accentColor: string;
}

interface CalendarColorRegistry {
  accentByKey: Map<string, string>;
  userIdToKey: Map<string, string>;
  doctorIdToKey: Map<string, string>;
  legendItems: CalendarLegendItem[];
}

interface CalendarColorSource {
  doctorId?: string;
  userId?: string;
  calendarName?: string;
  displayName?: string;
  label?: string;
  agendaColor?: string | null;
}

const AGENDA_STATUS_COLORS: Record<AgendaEventUiStatus, string> = {
  scheduled: '#6b7280',
  no_show: '#ef4444',
  no_evaluated: '#f59e0b',
  no_billed: '#8b5cf6',
  completed: '#10b981',
  cancelled: '#ef4444',
};

const AGENDA_STATUS_LABELS: Record<AgendaEventUiStatus, string> = {
  scheduled: 'Agendado',
  completed: 'Concluído',
  no_show: 'Falta',
  cancelled: 'Cancelado',
  no_billed: 'A faturar',
  no_evaluated: 'Sem evolução',
};

const parseAgendaEventStatus = (status: unknown): AgendaEventUiStatus => {
  if (typeof status !== 'string') {
    return 'scheduled';
  }

  const normalizedStatus = status.trim().toLowerCase().replace(/\s+/g, '_');
  if (
    normalizedStatus === 'scheduled' ||
    normalizedStatus === 'completed' ||
    normalizedStatus === 'no_show' ||
    normalizedStatus === 'cancelled' ||
    normalizedStatus === 'no_billed' ||
    normalizedStatus === 'no_evaluated'
  ) {
    return normalizedStatus;
  }

  return 'scheduled';
};

const parseAppointmentToAgendaEvent = (appointment: AgendaAppointmentRow): AgendaEvent => {
  const startDate = new Date(appointment.appointmentStartDate);
  const endDate = new Date(appointment.appointmentEndDate);
  const normalizedLocation = normalizeAppointmentLocation(appointment.appointmentLocation);
  const locationIssue = describeAppointmentLocationIssue(appointment.appointmentLocation);

  return {
    id: appointment.appointmentId,
    title: String(appointment.appointmentTitle || '').trim(),
    start: startDate,
    end: endDate,
    doctorId: appointment.doctorId || undefined,
    userId: appointment.userId || undefined,
    calendar_name: String(
      appointment.users?.userAssignedCalendarName ||
      appointment.users?.userDisplayName ||
      '',
    ).trim(),
    location: normalizedLocation || undefined,
    rawLocation: appointment.appointmentLocation ?? null,
    locationIssue,
    patientId: appointment.patientId || undefined,
    status: parseAgendaEventStatus(appointment.appointmentStatus),
    evolutionId: appointment.appointmentEvolutionId || undefined,
    invoiceId: appointment.invoiceId || undefined,
    financialInvoiceId: appointment.financialInvoiceId || undefined,
    isBilled: Boolean(appointment.appointmentIsBilled),
    financialAppointmentId: appointment.financialAppointmentId || undefined,
    serviceId: appointment.appointmentServiceId || undefined,
    serviceName: appointment.appointmentServiceNameSnapshot || undefined,
  };
};

const resolveAgendaEventUiStatus = (event?: AgendaEvent | null): AgendaEventUiStatus => {
  if (!event) {
    return 'scheduled';
  }

  const rawStatus = parseAgendaEventStatus(event.status);
  const hasLinkedEvolution = Boolean(event.evolutionId);
  const isBilled = Boolean(event.isBilled);

  if (rawStatus === 'cancelled' || rawStatus === 'no_show' || rawStatus === 'scheduled') {
    return rawStatus;
  }

  if (hasLinkedEvolution) {
    return isBilled ? 'completed' : 'no_billed';
  }

  if (rawStatus === 'completed' || rawStatus === 'no_evaluated' || isBilled) {
    return 'no_evaluated';
  }

  return 'scheduled';
};

const getEditSectionMessage = (status: AgendaEventUiStatus): string => {
  if (status === 'completed') {
    return 'Agendamento concluído com evolução registrada e faturamento realizado';
  }
  if (status === 'cancelled' || status === 'no_show') {
    return 'Agendamento cancelado ou paciente não compareceu na data prevista';
  }
  if (status === 'no_billed') {
    return 'Agendamento realizado com evolução registrada mas sem faturamento';
  }
  if (status === 'no_evaluated') {
    return 'Agendamento faturado, porém sem evolução registrada';
  }
  return 'Agendamento em aberto e disponível para edição.';
};

const getStatusManagementMessage = (status: AgendaEventUiStatus): string => {
  if (status === 'scheduled') {
    return 'Atualize o status do agendamento. Marcar como "Concluído" irá consumir uma sessão do pacote do paciente ou gerar uma cobrança avulsa, conforme as regras de negócio.';
  }
  if (status === 'no_billed') {
    return 'Agendamento realizado com evolução registrada, porém sem faturamento realizado';
  }
  if (status === 'no_evaluated') {
    return 'Agendamento faturado, mas ainda sem evolução registrada';
  }
  if (status === 'no_show') {
    return 'Situação: Falta';
  }
  if (status === 'completed') {
    return 'Situação: Concluído';
  }
  if (status === 'cancelled') {
    return 'Situação: Cancelado';
  }
  return 'Situação: Sem evolução';
};

const buildPatientFullAddress = (patient: any): string => {
  if (!patient) return '';

  const line1 = [
    patient.patientAddressStreet,
    patient.patientAddressNumber,
  ]
    .filter(Boolean)
    .join(', ');

  const line2 = [
    patient.patientAddressComplement,
    patient.patientAddressNeighborhood,
  ]
    .filter(Boolean)
    .join(' - ');

  const line3 = [
    patient.patientAddressCity && patient.patientAddressState
      ? `${patient.patientAddressCity} - ${patient.patientAddressState}`
      : patient.patientAddressCity || patient.patientAddressState,
    patient.patientAddressZipCode ? `CEP: ${patient.patientAddressZipCode}` : '',
  ]
    .filter(Boolean)
    .join(', ');

  return [line1, line2, line3].filter(Boolean).join(', ');
};

interface AgendaPatientDisplaySource {
  nome?: string | null;
  sobrenome?: string | null;
  patientFullName?: string | null;
  personalData?: { name?: string | null } | null;
  name?: string | null;
}

const getPatientDisplayName = (patient?: AgendaPatientDisplaySource | null): string => {
  const composedName = `${patient?.nome ?? ''} ${patient?.sobrenome ?? ''}`.trim();
  return composedName
    || String(patient?.patientFullName || '').trim()
    || String(patient?.personalData?.name || '').trim()
    || String(patient?.name || '').trim()
    || 'Paciente sem nome';
};

interface CreateForm {
  subject: string;
  patientId: string;     // ID do paciente selecionado
  calendar_name: string;
  location: string;
  start: string; // datetime-local
  end: string;   // datetime-local
  serviceId: string;
}

interface AgendaServiceOption {
  serviceId: string;
  serviceName: string;
  serviceCode?: string | null;
}

type SeriesScheduleMode = 'weekly' | 'specific';

interface WeeklySeriesSlotForm extends WeeklyAppointmentSlot {
  id: string;
}

interface SpecificSeriesSlotForm extends SpecificAppointmentSlot {
  id: string;
}

const WEEKDAY_OPTIONS = [
  { value: 1, label: 'Segunda-feira' },
  { value: 2, label: 'Terça-feira' },
  { value: 3, label: 'Quarta-feira' },
  { value: 4, label: 'Quinta-feira' },
  { value: 5, label: 'Sexta-feira' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' },
] as const;

type AgendaAssignableRole = 'doctor' | 'fisioterapeuta' | 'interno';

interface ProfessionalOption {
  doctorId?: string | null;
  userId: string;
  userRole: AgendaAssignableRole;
  accessStartAt?: string | null;
  accessEndAt?: string | null;
  isAccessActive: boolean;
  calendarName: string;
  displayName: string;
  label: string;
  agendaColor?: string | null;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const ALLOWED_AGENDA_ROLES: AgendaAssignableRole[] = ['doctor', 'fisioterapeuta', 'interno'];

const relationRows = <T,>(value: T | T[] | null | undefined): T[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const buildProfessionalOption = ({
  doctorId,
  userId,
  userRole,
  accessStartAt,
  accessEndAt,
  calendarName,
  displayName,
  agendaColor,
}: {
  doctorId?: string | null;
  userId: string;
  userRole: AgendaAssignableRole;
  accessStartAt?: string | null;
  accessEndAt?: string | null;
  calendarName?: string | null;
  displayName?: string | null;
  agendaColor?: string | null;
}): ProfessionalOption | null => {
  if (userRole !== 'interno' && !doctorId) {
    return null;
  }

  const normalizedCalendarName = String(calendarName || '').trim();
  const normalizedDisplayName = String(displayName || '').trim();
  const label = normalizedCalendarName || normalizedDisplayName || `Profissional ${String(userId).slice(0, 8)}`;

  return {
    doctorId: doctorId || null,
    userId,
    userRole,
    accessStartAt: accessStartAt || null,
    accessEndAt: accessEndAt || null,
    isAccessActive: isClientAccessActive(accessStartAt || null, accessEndAt || null),
    calendarName: normalizedCalendarName,
    displayName: normalizedDisplayName,
    label,
    agendaColor: normalizeAgendaColor(agendaColor),
  };
};

const VIEW_LABELS: Record<string, string> = {
  [Views.AGENDA]: 'Agendamentos',
  [Views.DAY]: 'Dia',
  [Views.WEEK]: 'Semana',
  [Views.MONTH]: 'Mês',
};

const AGENDA_VIEW_ORDER: View[] = [Views.AGENDA, Views.DAY, Views.WEEK, Views.MONTH];
const AGENDA_START_HOUR = 4;
const AGENDA_END_HOUR = 22;
const DISTINCT_COLOR_STEP = 137.508;
const DISTINCT_COLOR_SATURATION_LEVELS = [72, 66, 78, 60];
const DISTINCT_COLOR_LIGHTNESS_LEVELS = [58, 52, 64, 48];

const normalizeCalendarLabel = (value?: string | null): string =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const hashLabel = (value: string): number =>
  value.split('').reduce((acc, char) => ((acc * 31) + char.charCodeAt(0)) >>> 0, 7);

const getCalendarLabelPriority = (label: string): number => {
  if (!label || label === 'Agenda sem nome') {
    return 0;
  }
  if (label.startsWith('Profissional ')) {
    return 1;
  }
  return 2;
};

const hslToHex = (hue: number, saturation: number, lightness: number): string => {
  const normalizedHue = ((hue % 360) + 360) % 360;
  const s = Math.max(0, Math.min(100, saturation)) / 100;
  const l = Math.max(0, Math.min(100, lightness)) / 100;
  const chroma = (1 - Math.abs((2 * l) - 1)) * s;
  const huePrime = normalizedHue / 60;
  const x = chroma * (1 - Math.abs((huePrime % 2) - 1));

  let red = 0;
  let green = 0;
  let blue = 0;

  if (huePrime >= 0 && huePrime < 1) {
    red = chroma;
    green = x;
  } else if (huePrime < 2) {
    red = x;
    green = chroma;
  } else if (huePrime < 3) {
    green = chroma;
    blue = x;
  } else if (huePrime < 4) {
    green = x;
    blue = chroma;
  } else if (huePrime < 5) {
    red = x;
    blue = chroma;
  } else {
    red = chroma;
    blue = x;
  }

  const match = l - (chroma / 2);
  const toHex = (channel: number) => Math.max(0, Math.min(255, Math.round((channel + match) * 255)))
    .toString(16)
    .padStart(2, '0');

  return `#${toHex(red)}${toHex(green)}${toHex(blue)}`;
};

const buildDistinctCalendarAccentColor = (seed: string, usedTokens: Set<string>): string => {
  const baseHash = hashLabel(seed);
  let attempt = 0;

  while (attempt < 4096) {
    const hue = Math.round((baseHash + (attempt * DISTINCT_COLOR_STEP)) % 360);
    const saturation = DISTINCT_COLOR_SATURATION_LEVELS[attempt % DISTINCT_COLOR_SATURATION_LEVELS.length];
    const lightness = DISTINCT_COLOR_LIGHTNESS_LEVELS[
      Math.floor(attempt / DISTINCT_COLOR_SATURATION_LEVELS.length) % DISTINCT_COLOR_LIGHTNESS_LEVELS.length
    ];
    const token = `${hue}-${saturation}-${lightness}`;

    if (!usedTokens.has(token)) {
      usedTokens.add(token);
      return hslToHex(hue, saturation, lightness);
    }

    attempt += 1;
  }

  return hslToHex(baseHash % 360, DISTINCT_COLOR_SATURATION_LEVELS[0], DISTINCT_COLOR_LIGHTNESS_LEVELS[0]);
};

const getCalendarSourceLabel = (source: CalendarColorSource): string => {
  const calendarName = String(source.calendarName || '').trim();
  if (calendarName) {
    return calendarName;
  }

  const displayName = String(source.displayName || '').trim();
  if (displayName) {
    return displayName;
  }

  const label = String(source.label || '').trim();
  if (label) {
    return label;
  }

  if (source.userId) {
    return `Profissional ${String(source.userId).slice(0, 8)}`;
  }

  if (source.doctorId) {
    return `Profissional ${String(source.doctorId).slice(0, 8)}`;
  }

  return 'Agenda sem nome';
};

const getEventCalendarLabel = (event: AgendaEvent): string => {
  const calendarName = String(event.calendar_name || '').trim();
  if (calendarName) {
    return calendarName;
  }

  if (event.userId) {
    return `Profissional ${String(event.userId).slice(0, 8)}`;
  }

  if (event.doctorId) {
    return `Profissional ${String(event.doctorId).slice(0, 8)}`;
  }

  return 'Agenda sem nome';
};

const getProfessionalRegistryKey = (professional: CalendarColorSource): string =>
  professional.userId
    ? `user:${professional.userId}`
    : professional.doctorId
      ? `doctor:${professional.doctorId}`
      : `label:${normalizeCalendarLabel(getCalendarSourceLabel(professional)) || 'agenda-sem-nome'}`;

const getEventRegistryKey = (
  event: AgendaEvent,
  registry: Pick<CalendarColorRegistry, 'userIdToKey' | 'doctorIdToKey'>,
): string => {
  const label = getEventCalendarLabel(event);

  return (event.userId && registry.userIdToKey.get(event.userId))
    || (event.doctorId && registry.doctorIdToKey.get(event.doctorId))
    || (event.userId ? `user:${event.userId}` : '')
    || (event.doctorId ? `doctor:${event.doctorId}` : '')
    || `label:${normalizeCalendarLabel(label) || 'agenda-sem-nome'}`;
};

const createCalendarColorRegistry = (
  professionals: CalendarColorSource[],
  events: AgendaEvent[],
): CalendarColorRegistry => {
  const entries = new Map<string, CalendarColorEntry>();
  const userIdToKey = new Map<string, string>();
  const doctorIdToKey = new Map<string, string>();

  const registerEntry = (key: string, label: string, accentColor?: string | null) => {
    const trimmedLabel = String(label || '').trim() || 'Agenda sem nome';
    const sortLabel = normalizeCalendarLabel(trimmedLabel) || trimmedLabel.toLowerCase();
    const normalizedAccentColor = normalizeAgendaColor(accentColor);
    const existing = entries.get(key);

    const nextEntry: CalendarColorEntry = {
      key,
      label: existing?.label || trimmedLabel,
      sortLabel: existing?.sortLabel || sortLabel,
      accentColor: existing?.accentColor || null,
    };

    if (!existing || getCalendarLabelPriority(trimmedLabel) > getCalendarLabelPriority(existing.label)) {
      nextEntry.label = trimmedLabel;
      nextEntry.sortLabel = sortLabel;
    }

    if (normalizedAccentColor) {
      nextEntry.accentColor = normalizedAccentColor;
    }

    entries.set(key, nextEntry);
  };

  professionals.forEach((professional) => {
    const key = getProfessionalRegistryKey(professional);

    registerEntry(key, getCalendarSourceLabel(professional), professional.agendaColor);

    if (professional.userId) {
      userIdToKey.set(professional.userId, key);
    }
    if (professional.doctorId) {
      doctorIdToKey.set(professional.doctorId, key);
    }
  });

  events.forEach((event) => {
    const label = getEventCalendarLabel(event);
    const key = getEventRegistryKey(event, { userIdToKey, doctorIdToKey });

    registerEntry(key, label);

    if (event.userId && !userIdToKey.has(event.userId)) {
      userIdToKey.set(event.userId, key);
    }
    if (event.doctorId && !doctorIdToKey.has(event.doctorId)) {
      doctorIdToKey.set(event.doctorId, key);
    }
  });

  const sortedEntries = Array.from(entries.values())
    .sort((left, right) => left.label.localeCompare(right.label, 'pt-BR'));
  const usedTokens = new Set<string>();
  const accentByKey = new Map<string, string>();

  sortedEntries.forEach((entry) => {
    accentByKey.set(
      entry.key,
      entry.accentColor || buildDistinctCalendarAccentColor(`${entry.key}:${entry.sortLabel}`, usedTokens),
    );
  });

  return {
    accentByKey,
    userIdToKey,
    doctorIdToKey,
    legendItems: sortedEntries.map((entry) => ({
      id: entry.key,
      label: entry.label,
      accentColor: accentByKey.get(entry.key) || '#64748b',
    })),
  };
};

const hexToRgba = (hex: string, alpha: number): string => {
  const sanitizedHex = hex.replace('#', '').trim();
  const fullHex = sanitizedHex.length === 3
    ? sanitizedHex.split('').map((char) => `${char}${char}`).join('')
    : sanitizedHex;

  const red = Number.parseInt(fullHex.slice(0, 2), 16);
  const green = Number.parseInt(fullHex.slice(2, 4), 16);
  const blue = Number.parseInt(fullHex.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const formatShortToken = (value: string): string => {
  if (!value) {
    return value;
  }

  return value.charAt(0).toUpperCase() + value.slice(1).replace('.', '');
};

const formatCompactMonth = (date: Date, timeZone: string): string =>
  formatShortToken(moment.tz(date, timeZone).format('MMM'));

const formatWeekHeaderLabel = (date: Date, timeZone: string): string =>
  `${moment.tz(date, timeZone).format('DD')} ${formatShortToken(moment.tz(date, timeZone).format('ddd'))}`;

const formatToolbarRangeLabel = (date: Date, view: View, timeZone: string): string => {
  const baseDate = moment.tz(date, timeZone);

  if (view === Views.MONTH) {
    return formatShortToken(baseDate.format('MMMM YYYY'));
  }

  if (view === Views.DAY) {
    return `${formatShortToken(baseDate.format('dddd'))}, ${baseDate.format('DD')} ${formatCompactMonth(baseDate.toDate(), timeZone)} ${baseDate.format('YYYY')}`;
  }

  if (view === Views.AGENDA) {
    return `${formatShortToken(baseDate.format('dddd'))}, ${baseDate.format('DD')} ${formatCompactMonth(baseDate.toDate(), timeZone)} ${baseDate.format('YYYY')}`;
  }

  const rangeStart = baseDate.clone().startOf('week');
  const rangeEnd = baseDate.clone().endOf('week');

  return `${rangeStart.format('DD')} ${formatCompactMonth(rangeStart.toDate(), timeZone)} - ${rangeEnd.format('DD')} ${formatCompactMonth(rangeEnd.toDate(), timeZone)} ${rangeEnd.format('YYYY')}`;
};

const getVisibleCalendarRange = (date: Date, view: View, timeZone: string): { start: Date; end: Date } => {
  const baseDate = moment.tz(date, timeZone);

  if (view === Views.DAY) {
    return {
      start: baseDate.clone().startOf('day').toDate(),
      end: baseDate.clone().endOf('day').toDate(),
    };
  }

  if (view === Views.WEEK) {
    return {
      start: baseDate.clone().startOf('week').toDate(),
      end: baseDate.clone().endOf('week').toDate(),
    };
  }

  if (view === Views.AGENDA) {
    return {
      start: baseDate.clone().startOf('day').toDate(),
      end: baseDate.clone().endOf('day').toDate(),
    };
  }

  return {
    start: baseDate.clone().startOf('month').startOf('week').toDate(),
    end: baseDate.clone().endOf('month').endOf('week').toDate(),
  };
};

const isEventInVisibleCalendarRange = (event: AgendaEvent, date: Date, view: View, timeZone: string): boolean => {
  const { start, end } = getVisibleCalendarRange(date, view, timeZone);
  return event.start <= end && event.end >= start;
};

const getAgendaEventTooltip = (event: AgendaEvent, timeZone: string): string =>
  [
    event.title,
    `${moment.tz(event.start, timeZone).format('DD/MM/YYYY HH:mm')} - ${moment.tz(event.end, timeZone).format('HH:mm')}`,
    `Status: ${AGENDA_STATUS_LABELS[resolveAgendaEventUiStatus(event)]}`,
    event.calendar_name ? `Profissional: ${event.calendar_name}` : '',
    event.location
      ? `Local: ${formatAppointmentLocation(event.location)}`
      : event.locationIssue
      ? `Local: ${event.locationIssue}`
      : '',
  ]
    .filter(Boolean)
    .join(' • ');

const AgendaToolbar = ({
  date,
  view,
  views,
  onNavigate,
  onView,
  timeZone,
}: {
  date: Date;
  view: View;
  views: string[] | Record<string, unknown>;
  onNavigate: (action: 'PREV' | 'NEXT' | 'TODAY') => void;
  onView: (view: View) => void;
  timeZone: string;
}) => {
  const availableViews = Array.isArray(views) ? views : Object.keys(views || {});

  return (
    <div className="mb-5 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm lg:flex-row lg:items-center lg:justify-between">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onNavigate('TODAY')}
          className="rounded-xl border border-slate-200/80 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
        >
          Hoje
        </button>
        <div className="flex items-center gap-1 rounded-xl border border-slate-200/80 bg-white p-1 shadow-sm">
          <button
            type="button"
            onClick={() => onNavigate('PREV')}
            className="rounded-lg p-2 text-slate-600 transition hover:bg-slate-100"
            aria-label="Período anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onNavigate('NEXT')}
            className="rounded-lg p-2 text-slate-600 transition hover:bg-slate-100"
            aria-label="Próximo período"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="px-1 text-center text-base font-semibold text-slate-700 lg:text-lg">
        {formatToolbarRangeLabel(date, view, timeZone)}
      </div>

      <div className="flex flex-wrap items-center gap-1 rounded-2xl border border-slate-200/80 bg-white/85 p-1 shadow-sm">
        {availableViews
          .filter((candidateView) => VIEW_LABELS[candidateView])
          .map((candidateView) => {
            const isActive = candidateView === view;

            return (
              <button
                key={candidateView}
                type="button"
                onClick={() => onView(candidateView as View)}
                className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
                  isActive
                    ? 'bg-slate-200 text-slate-900 shadow-inner'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                {VIEW_LABELS[candidateView]}
              </button>
            );
          })}
      </div>
    </div>
  );
};

const getAgendaStatusIcon = (status: AgendaEventUiStatus) => {
  if (status === 'completed') return CheckCircle;
  if (status === 'no_evaluated') return AlertTriangle;
  if (status === 'no_billed') return DollarSign;
  if (status === 'no_show' || status === 'cancelled') return XCircle;
  return Clock;
};

const AgendaEventCard = ({ event }: { event: AgendaEvent }) => {
  const resolvedStatus = resolveAgendaEventUiStatus(event);
  const StatusIcon = getAgendaStatusIcon(resolvedStatus);
  const professionalLabel = getEventCalendarLabel(event);

  return (
    <div className="agenda-event-card flex h-full min-h-[34px] flex-col justify-center overflow-hidden px-2.5 py-1.5">
      <div className="flex items-center gap-2">
        <StatusIcon
          className="h-3.5 w-3.5 shrink-0"
          style={{ color: AGENDA_STATUS_COLORS[resolvedStatus] }}
        />
        <p className="agenda-event-title truncate text-[12px] font-semibold leading-tight text-slate-900">
          {event.title || 'Paciente sem nome'}
        </p>
      </div>
      <div className="agenda-event-subtitle mt-1 flex items-center gap-2 text-[11px] text-slate-500">
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: 'var(--agenda-calendar-color)' }}
          title={`Agenda: ${professionalLabel}`}
        />
        <p className="truncate">{professionalLabel}</p>
      </div>
    </div>
  );
};

const AgendaListEventCard = ({ event }: { event: AgendaEvent }) => (
  <div className="flex flex-col gap-1 px-4 py-3">
    <p className="text-sm font-semibold leading-tight text-slate-900">
      {event.title}
    </p>
    <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-500">
      {`Profissional: ${getEventCalendarLabel(event)}`}
    </p>
  </div>
);

const AgendaPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, selectedClient } = useAuth();
  const { profile, isLoading: isClinicProfileLoading } = useClinicProfile();
  const activeClientId = selectedClient?.clientId || user?.current_client_id || null;
  const persistedProfileTimeZone = profile?.timeZone;
  const isClinicTimeZoneReady = profile?.clientId === activeClientId
    && !isClinicProfileLoading
    && isValidTimeZone(persistedProfileTimeZone);
  const timeZone = isClinicTimeZoneReady
    ? persistedProfileTimeZone.trim()
    : DEFAULT_TIME_ZONE;
  const localizer = useMemo(
    () => createAgendaTimeZoneLocalizer(timeZone),
    [timeZone],
  );
  const clinicTemporalContextRef = useRef({
    clientId: activeClientId,
    timeZone,
    isReady: isClinicTimeZoneReady,
  });
  clinicTemporalContextRef.current = {
    clientId: activeClientId,
    timeZone,
    isReady: isClinicTimeZoneReady,
  };
  const activeRole = selectedClient?.userRole || user?.userRole || null;
  const isInternalUser = activeRole === 'interno';
  const canManageAgenda = activeRole === 'admin' || activeRole === 'doctor' || activeRole === 'fisioterapeuta';
  const canEditAgendaEntries = activeRole === 'admin';
  const canDeleteAgendaEntries = activeRole === 'admin';
  const canManageAppointmentStatus = activeRole === 'admin' || activeRole === 'doctor' || activeRole === 'fisioterapeuta';
  const canUseEvolutionShortcut = activeRole === 'admin' || activeRole === 'doctor' || activeRole === 'fisioterapeuta' || activeRole === 'interno';

  // Proteção contra desmontagem não autorizada
  useEffect(() => {
    console.log("🔵 AgendaPage: Estado de autenticação:", { isAuthenticated, userRole: user?.userRole });
  }, [isAuthenticated, user]);

  const [events, setEvents] = useState<AgendaEvent[]>([]);
  const agendaEventsRequestIdRef = useRef(0);
  const [isLoading, setIsLoading] = useState(true);
  const [currentView, setCurrentView] = useState<View>(Views.AGENDA);
  const [currentDate, setCurrentDate] = useState(new Date());
  // const [firebaseConnected, setFirebaseConnected] = useState<boolean | null>(null); // Removido
  const [selectedEvent, setSelectedEvent] = useState<AgendaEvent | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [absenceDecisionPreview, setAbsenceDecisionPreview] = useState<AbsenceDecisionPreview | null>(null);
  const [absenceDecisionAppointmentId, setAbsenceDecisionAppointmentId] = useState('');
  const [absenceIdempotencyKey, setAbsenceIdempotencyKey] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState<Set<string>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [patients, setPatients] = useState<any[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [showListView, setShowListView] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [isUpdating, setIsUpdating] = useState(false); // 🆕 Novo estado para controle de UI
  const [activeCalendarFilterKey, setActiveCalendarFilterKey] = useState<string>('all');
  const [showLegendPanel, setShowLegendPanel] = useState(false);
  const [showReopenSelectionModal, setShowReopenSelectionModal] = useState(false);
  const [reopenPreview, setReopenPreview] = useState<ReopenNoEvaluatedPreviewResult | null>(null);
  const [selectedReopenCandidateId, setSelectedReopenCandidateId] = useState('');
  const [showFinancialOriginModal, setShowFinancialOriginModal] = useState(false);
  const [financialOriginPreview, setFinancialOriginPreview] = useState<FinancialOriginPreview | null>(null);
  const [selectedFinancialOriginId, setSelectedFinancialOriginId] = useState('');
  const [selectedFinancialOriginKind, setSelectedFinancialOriginKind] = useState<FinancialOriginKind>('pacote');
  const [pendingFinancialAppointmentId, setPendingFinancialAppointmentId] = useState('');
  const [appointmentAuditTrail, setAppointmentAuditTrail] = useState<
    Awaited<ReturnType<typeof AppointmentStatusService.getResolutionAuditTrail>>
  >([]);
  const [loadingAppointmentAuditTrail, setLoadingAppointmentAuditTrail] = useState(false);
  const [showAttendanceCertificateModal, setShowAttendanceCertificateModal] = useState(false);
  const [attendanceCertificateData, setAttendanceCertificateData] = useState<AttendanceCertificateFormData | null>(null);
  const [isGeneratingAttendanceCertificate, setIsGeneratingAttendanceCertificate] = useState(false);

  // Estados para filtro de período na lista
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');

  // Estados para seleção de período antes de mostrar resultados (apenas para lista)
  const [dateRangeConfirmed, setDateRangeConfirmed] = useState(false);
  const [pendingStartDate, setPendingStartDate] = useState('');
  const [pendingEndDate, setPendingEndDate] = useState('');
  
  // Profissionais disponíveis para agendamento
  const [calendars, setCalendars] = useState<string[]>([]);
  const [professionals, setProfessionals] = useState<ProfessionalOption[]>([]);
  const [agendaServices, setAgendaServices] = useState<AgendaServiceOption[]>([]);
  const [loadingAgendaServices, setLoadingAgendaServices] = useState(false);
  const [useCustomCalendarName, setUseCustomCalendarName] = useState(false);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createMode, setCreateMode] = useState<AppointmentCreateMode>('single');
  const [seriesScheduleMode, setSeriesScheduleMode] = useState<SeriesScheduleMode>('weekly');
  const [seriesInitialWeekDate, setSeriesInitialWeekDate] = useState('');
  const [seriesWeekCount, setSeriesWeekCount] = useState(4);
  const [weeklySeriesSlots, setWeeklySeriesSlots] = useState<WeeklySeriesSlotForm[]>([]);
  const [specificSeriesSlots, setSpecificSeriesSlots] = useState<SpecificSeriesSlotForm[]>([]);
  const [groupPatientIds, setGroupPatientIds] = useState<string[]>([]);
  const [groupPatientPickerId, setGroupPatientPickerId] = useState('');
  const [createFormTimeZone, setCreateFormTimeZone] = useState(DEFAULT_TIME_ZONE);
  const [createFormClientId, setCreateFormClientId] = useState<string | null>(null);
  const [isCreatingAppointments, setIsCreatingAppointments] = useState(false);
  const createSubmissionLockRef = useRef(false);
  const createBatchAttemptRef = useRef<{ batchId: string; fingerprint: string } | null>(null);
  const [form, setForm] = useState<CreateForm>({
    subject: '',
    patientId: '',
    calendar_name: '',
    location: '',
    start: '',
    end: '',
    serviceId: '',
  });

  // 🆕 Estados para edição de agendamentos
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<AgendaEvent | null>(null);
  const [editFormTimeZone, setEditFormTimeZone] = useState(DEFAULT_TIME_ZONE);
  const [editFormClientId, setEditFormClientId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<CreateForm>({
    subject: '',
    patientId: '',
    calendar_name: '',
    location: '',
    start: '',
    end: '',
    serviceId: '',
  });

  const getPatientLifecycleStatus = (patient: any): 'active' | 'inactive' | 'archived' => {
    const status = patient?.patientLifecycleStatus ?? patient?.lifecycleStatus ?? 'active';
    if (status === 'inactive' || status === 'archived') return status;
    return 'active';
  };

  const getPatientNewCareBlockReason = (patient: any): 'lifecycle' | 'clinical_inactive' | null => {
    const lifecycleStatus = getPatientLifecycleStatus(patient);
    if (lifecycleStatus !== 'active') return 'lifecycle';
    const financialActive = typeof patient?.financialProfile?.active === 'boolean'
      ? patient.financialProfile.active
      : true;
    if (!financialActive) return 'clinical_inactive';
    return null;
  };

  const confirmPersistedTimeZoneForWrite = useCallback(async (
    expectedTimeZone: string,
    expectedClientId: string | null,
  ): Promise<string> => {
    const contextStillMatches = () => {
      const currentContext = clinicTemporalContextRef.current;
      return currentContext.isReady
        && currentContext.clientId === expectedClientId
        && currentContext.timeZone === expectedTimeZone;
    };

    if (
      !expectedClientId
      || !contextStillMatches()
      || !isValidTimeZone(expectedTimeZone)
    ) {
      throw new Error('Não foi possível confirmar o fuso horário da clínica. Recarregue a página antes de continuar.');
    }

    try {
      const persistedTimeZone = await ClinicTimeZoneService.requireCurrentForWrite(
        expectedClientId,
        expectedTimeZone,
      );
      if (!contextStillMatches()) {
        throw new Error('A clínica ou o fuso horário mudou durante a operação. Nenhuma alteração foi salva.');
      }
      return persistedTimeZone;
    } catch (error) {
      console.error('Erro ao revalidar o fuso horário antes da gravação:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Não foi possível revalidar o fuso horário da clínica. Nenhuma alteração foi salva.');
    }
  }, []);

  const openCreateModalForRange = useCallback((startDate: Date, endDate: Date) => {
    if (!isClinicTimeZoneReady) {
      toast.error('Aguarde a confirmação do fuso horário da clínica antes de agendar.');
      return;
    }

    const initialDate = formatInTimeZone(startDate, 'YYYY-MM-DD', timeZone);
    const initialStartTime = formatInTimeZone(startDate, 'HH:mm', timeZone);
    const initialEndTime = formatInTimeZone(endDate, 'HH:mm', timeZone);
    const nextSpecificDate = formatInTimeZone(
      addCalendarDaysInTimeZone(startDate, 7, timeZone),
      'YYYY-MM-DD',
      timeZone,
    );

    setCreateMode('single');
    setCreateFormTimeZone(timeZone);
    setCreateFormClientId(activeClientId);
    setSeriesScheduleMode('weekly');
    setSeriesInitialWeekDate(initialDate);
    setSeriesWeekCount(4);
    setWeeklySeriesSlots([{
      id: createAppointmentBatchId(),
      weekday: Number(formatInTimeZone(startDate, 'E', timeZone)),
      startTime: initialStartTime,
      endTime: initialEndTime,
    }]);
    setSpecificSeriesSlots([
      {
        id: createAppointmentBatchId(),
        date: initialDate,
        startTime: initialStartTime,
        endTime: initialEndTime,
      },
      {
        id: createAppointmentBatchId(),
        date: nextSpecificDate,
        startTime: initialStartTime,
        endTime: initialEndTime,
      },
    ]);
    setGroupPatientIds([]);
    setGroupPatientPickerId('');
    createBatchAttemptRef.current = null;
    setForm({
      subject: '',
      patientId: '',
      calendar_name: '',
      location: '',
      start: toDateTimeLocalInTimeZone(startDate, timeZone),
      end: toDateTimeLocalInTimeZone(endDate, timeZone),
      serviceId: '',
    });
    setShowCreateModal(true);
  }, [activeClientId, isClinicTimeZoneReady, timeZone]);

  const closeCreateModal = useCallback(() => {
    createBatchAttemptRef.current = null;
    setShowCreateModal(false);
  }, []);

  const hasEvolution = (event?: AgendaEvent | null) => {
    if (!event) {
      return false;
    }

    const resolvedStatus = resolveAgendaEventUiStatus(event);
    return Boolean(event.evolutionId) || resolvedStatus === 'completed' || resolvedStatus === 'no_billed';
  };

  const getMedicalRecordPath = useCallback(() => {
    const tenantSlug = selectedClient?.clientSlug;

    switch (activeRole) {
      case 'admin':
      case 'interno':
        return buildTenantPath('/admin/prontuario', tenantSlug);
      case 'fisioterapeuta':
        return buildTenantPath('/fisioterapeuta/prontuario', tenantSlug);
      default:
        return null;
    }
  }, [activeRole, selectedClient?.clientSlug]);

  const handleOpenMedicalRecordFromAppointment = useCallback((event: AgendaEvent | null) => {
    if (!event) {
      toast.error('Nenhum agendamento selecionado.');
      return;
    }
    if (!canUseEvolutionShortcut) {
      toast.error('O perfil atual não possui acesso a este atalho de evolução.');
      return;
    }
    const medicalRecordPath = getMedicalRecordPath();
    if (!medicalRecordPath) {
      toast.error('O perfil atual não possui acesso configurado ao prontuário.');
      return;
    }
    if (!event.patientId) {
      toast.error('Vincule um paciente ao agendamento antes de registrar a evolução.');
      return;
    }
    const eventStatus = resolveAgendaEventUiStatus(event);
    if (eventStatus === 'cancelled' || eventStatus === 'no_show') {
      toast.error('Agendamentos cancelados ou com falta não podem ser evoluídos.');
      return;
    }
    if (hasEvolution(event)) {
      toast.info('Este agendamento já possui evolução registrada.');
      return;
    }
    setShowEventModal(false);
    setSelectedEvent(null);
    navigate(medicalRecordPath, {
      state: {
        preSelectedPatient: event.patientId,
        preSelectedAppointment: event.id,
      },
    });
  }, [canUseEvolutionShortcut, getMedicalRecordPath, navigate]);

  const calendarColorRegistry = createCalendarColorRegistry(professionals, events);
  const selectableProfessionals = professionals.filter((professional) => professional.isAccessActive);
  const activeLegendKeys = new Set(
    selectableProfessionals.map((professional) => getProfessionalRegistryKey(professional))
  );
  const visibleEventLegendKeys = new Set(
    events
      .filter((event) => isEventInVisibleCalendarRange(event, currentDate, currentView, timeZone))
      .map((event) => getEventRegistryKey(event, calendarColorRegistry))
  );
  const calendarFilterOptions = calendarColorRegistry.legendItems.filter((item) =>
    activeLegendKeys.has(item.id) || visibleEventLegendKeys.has(item.id)
  );
  const matchesCalendarFilter = (event: AgendaEvent): boolean =>
    activeCalendarFilterKey === 'all'
      || getEventRegistryKey(event, calendarColorRegistry) === activeCalendarFilterKey;
  const filteredCalendarEvents = events.filter(matchesCalendarFilter);

  // Filtrar eventos por período (usado apenas na visualização em lista)
  const filteredEvents = events.filter(event => {
    try {
      if (!matchesCalendarFilter(event)) return false;
      if (!startDateFilter && !endDateFilter) return true;
      if (!event?.start) return false;

      const eventDate = formatInTimeZone(event.start, 'YYYY-MM-DD', timeZone);

      if (startDateFilter && endDateFilter) {
        return eventDate >= startDateFilter && eventDate <= endDateFilter;
      } else if (startDateFilter) {
        return eventDate >= startDateFilter;
      } else if (endDateFilter) {
        return eventDate <= endDateFilter;
      }

      return true;
    } catch (error) {
      console.warn('Erro ao filtrar evento:', error);
      return false;
    }
  });

  const getEventCalendarAccentColor = (event: AgendaEvent): string => {
    const resolvedKey = getEventRegistryKey(event, calendarColorRegistry);

    return calendarColorRegistry.accentByKey.get(resolvedKey)
      || buildDistinctCalendarAccentColor(resolvedKey, new Set());
  };

  const buildSelectedSeriesRanges = (seriesTimeZone = createFormTimeZone): AppointmentDateRange[] => {
    if (seriesScheduleMode === 'weekly') {
      return buildWeeklyAppointmentRanges({
        initialWeekDate: seriesInitialWeekDate,
        weeks: seriesWeekCount,
        slots: weeklySeriesSlots,
        timeZone: seriesTimeZone,
      });
    }

    return buildSpecificAppointmentRanges(specificSeriesSlots, seriesTimeZone);
  };

  let seriesPreviewRanges: AppointmentDateRange[] = [];
  let seriesPreviewError = '';
  if (createMode === 'series') {
    try {
      seriesPreviewRanges = buildSelectedSeriesRanges();
    } catch (error: unknown) {
      seriesPreviewError = error instanceof Error ? error.message : 'Revise as datas e horários da série.';
    }
  }

  const canEmitAttendanceCertificate = (event?: AgendaEvent | null): boolean => {
    const resolvedStatus = resolveAgendaEventUiStatus(event);
    return resolvedStatus === 'completed' || resolvedStatus === 'no_billed' || resolvedStatus === 'no_evaluated';
  };

  const buildAttendanceCertificateData = useCallback(async (event: AgendaEvent): Promise<AttendanceCertificateFormData> => {
    const patient = patients.find((item: any) => item.id === event.patientId);
    const branding = activeClientId
      ? await ClientReportSettingsService.resolveForClient(activeClientId, selectedClient)
      : null;
    const authorDirectory = event.userId
      ? await fetchMedicalRecordAuthors([event.userId], activeClientId || '')
      : new Map();
    const professionalAuthor = event.userId ? authorDirectory.get(event.userId) ?? null : null;

    const organizationName =
      branding?.displayName ||
      profile?.displayName ||
      selectedClient?.clientName ||
      clinicInfo.reportBranding.displayName;
    const organizationAddressLine1 =
      branding?.addressLine1 ||
      profile?.addressLine1 ||
      clinicInfo.endereco.enderecoCompleto;
    const organizationAddressLine2 =
      branding?.addressLine2 ||
      profile?.addressLine2 ||
      `${clinicInfo.endereco.cidade} - ${clinicInfo.endereco.estado} | CEP: ${clinicInfo.endereco.cep}`;
    const organizationContactLine =
      branding?.contactLine ||
      [
        profile?.contactPhone ? `Tel: ${profile.contactPhone}` : `Tel: ${clinicInfo.contato.telefone}`,
        profile?.contactEmail ? `Email: ${profile.contactEmail}` : `Email: ${clinicInfo.contato.email}`,
      ].join(' | ');
    const issueLocation = profile?.addressCity && profile?.addressState
      ? `${profile.addressCity}/${profile.addressState}`
      : profile?.addressCity ||
        [patient?.patientAddressCity, patient?.patientAddressState].filter(Boolean).join('/') ||
        '';
    const professionalName = professionalAuthor?.name || event.calendar_name || '';
    const registration = professionalAuthor?.councilNumber?.trim() || '';

    return {
      tenantSlug: selectedClient?.clientSlug || null,
      organizationName,
      organizationAddressLine1,
      organizationAddressLine2,
      organizationContactLine,
      footerLegalLine: branding?.footerLegalLine || profile?.footerLegalLine || '',
      branding,
      signedByUserId: event.userId || user?.userId || null,
      patientName: patient?.patientFullName || event.title || '',
      patientCpf: patient?.patientCpf || '',
      patientRg: patient?.patientRg || '',
      patientAddress: buildPatientFullAddress(patient),
      patientMedicalRecordNumber:
        patient?.patientMedicalRecordNumber ||
        patient?.prontu_x00c1_rio ||
        patient?.prontuario ||
        patient?.patientCpf ||
        '',
      appointmentDate: moment(event.start).format('YYYY-MM-DD'),
      appointmentStartTime: moment(event.start).format('HH:mm'),
      appointmentEndTime: moment(event.end).format('HH:mm'),
      attendanceType: event.title || 'Sessão de atendimento',
      attendanceLocation: event.location ? formatAppointmentLocation(event.location) : '',
      issueDate: moment().format('YYYY-MM-DD'),
      issueLocation,
      professionalName,
      professionalRegistration: registration ? `CREFITO: ${registration}` : '',
      signatureLabel: professionalName,
      additionalNotes: '',
    };
  }, [
    activeClientId,
    patients,
    profile?.addressCity,
    profile?.addressLine1,
    profile?.addressLine2,
    profile?.addressState,
    profile?.contactEmail,
    profile?.contactPhone,
    profile?.displayName,
    profile?.footerLegalLine,
    user?.userId,
    selectedClient,
  ]);

  const prepareAttendanceCertificateForExport = useCallback(async (
    data: AttendanceCertificateFormData,
  ): Promise<AttendanceCertificateFormData> => {
    if (data.signature?.verificationToken) {
      return data;
    }

    if (!activeClientId) {
      throw new Error('Tenant não identificado para gerar o atestado assinado.');
    }

    const signerUserId = data.signedByUserId || user?.userId || null;
    if (!signerUserId) {
      throw new Error('Não foi possível identificar o profissional responsável pelo atendimento.');
    }

    const authorDirectory = await fetchMedicalRecordAuthors([signerUserId], activeClientId);
    const signer =
      authorDirectory.get(signerUserId) ||
      buildMedicalRecordAuthorSnapshot({
        userId: signerUserId,
        name: data.professionalName || null,
        role: selectedClient?.userRole || user?.userRole || null,
        crefito: data.professionalRegistration ? data.professionalRegistration.replace(/^CREFITO:\s*/i, '') : null,
      });

    const signedByName = signer.name || data.professionalName || 'Profissional responsável';
    const signedByRole = signer.professionalTitle || 'Profissional responsável pelo atendimento';
    const normalizedProfessionalRegistration = (() => {
      const rawRegistration = data.professionalRegistration?.trim() || '';
      if (!rawRegistration) {
        return signer.councilNumber ? `CREFITO: ${signer.councilNumber}` : null;
      }

      return /^CREFITO\b/i.test(rawRegistration)
        ? rawRegistration
        : `CREFITO: ${rawRegistration}`;
    })();
    const signedByRegistration =
      normalizedProfessionalRegistration;
    const nextSignature = await buildSignedDocumentMetadata({
      hashPayload: {
        documentType: 'attendance_certificate',
        organizationName: data.organizationName,
        organizationAddressLine1: data.organizationAddressLine1,
        organizationAddressLine2: data.organizationAddressLine2,
        organizationContactLine: data.organizationContactLine,
        footerLegalLine: data.footerLegalLine,
        patientName: data.patientName,
        patientCpf: data.patientCpf,
        patientRg: data.patientRg,
        patientAddress: data.patientAddress,
        patientMedicalRecordNumber: data.patientMedicalRecordNumber,
        appointmentDate: data.appointmentDate,
        appointmentStartTime: data.appointmentStartTime,
        appointmentEndTime: data.appointmentEndTime,
        attendanceType: data.attendanceType,
        attendanceLocation: data.attendanceLocation,
        issueDate: data.issueDate,
        issueLocation: data.issueLocation,
        professionalName: data.professionalName,
        professionalRegistration: normalizedProfessionalRegistration,
        signatureLabel: data.signatureLabel,
        additionalNotes: data.additionalNotes,
        signedByUserId: signerUserId,
        signedByName,
        signedByRole,
        signedByRegistration,
      },
      signedByUserId: signerUserId,
      signedByName,
      signedByRole,
      signedByRegistration,
      templateVersion: 1,
    });

    await DocumentVerificationService.registerAttendanceCertificateVerification({
      clientId: activeClientId,
      tenantSlug: data.tenantSlug || selectedClient?.clientSlug || null,
      tenantName: data.organizationName,
      documentTitle: 'Atestado de presença',
      patientId: null,
      patientName: data.patientName,
      patientMedicalRecordNumber: data.patientMedicalRecordNumber || data.patientCpf || null,
      professionalName: signedByName,
      professionalRole: signedByRole,
      professionalRegistration: signedByRegistration,
      signedAt: nextSignature.signedAt,
      documentHash: nextSignature.documentHash,
      templateVersion: nextSignature.templateVersion,
      snapshot: {
        organizationName: data.organizationName,
        organizationAddressLine1: data.organizationAddressLine1 || null,
        organizationAddressLine2: data.organizationAddressLine2 || null,
        organizationContactLine: data.organizationContactLine || null,
        footerLegalLine: data.footerLegalLine || null,
        patientName: data.patientName,
        patientMedicalRecordNumber: data.patientMedicalRecordNumber || data.patientCpf || null,
        patientCpf: data.patientCpf,
        patientRg: data.patientRg,
        patientAddress: data.patientAddress,
        appointmentDate: data.appointmentDate,
        appointmentStartTime: data.appointmentStartTime,
        appointmentEndTime: data.appointmentEndTime,
        attendanceType: data.attendanceType,
        attendanceLocation: data.attendanceLocation,
        issueDate: data.issueDate,
        issueLocation: data.issueLocation,
        professionalName: data.professionalName,
        professionalRegistration: normalizedProfessionalRegistration,
        signatureLabel: data.signatureLabel,
        additionalNotes: data.additionalNotes,
        signedByUserId: signerUserId,
      },
      token: nextSignature.verificationToken,
    });

    return {
      ...data,
      signature: nextSignature,
    };
  }, [
    activeClientId,
    selectedClient?.clientSlug,
    selectedClient?.userRole,
    user?.userId,
    user?.userRole,
  ]);

  const handleOpenAttendanceCertificate = useCallback(async (event: AgendaEvent) => {
    try {
      setIsGeneratingAttendanceCertificate(true);
      const nextData = await buildAttendanceCertificateData(event);
      setAttendanceCertificateData(nextData);
      setShowAttendanceCertificateModal(true);
    } catch (error: any) {
      console.error('Erro ao preparar atestado de presença:', error);
      toast.error(`Não foi possível preparar o atestado: ${error?.message || 'Erro desconhecido'}`);
    } finally {
      setIsGeneratingAttendanceCertificate(false);
    }
  }, [buildAttendanceCertificateData]);

  const handleDownloadAttendanceCertificate = useCallback(async (data: AttendanceCertificateFormData) => {
    try {
      setIsGeneratingAttendanceCertificate(true);
      const signedData = await prepareAttendanceCertificateForExport(data);
      setAttendanceCertificateData(signedData);
      await AttendanceCertificatePDFService.downloadPDF(signedData);
    } catch (error: any) {
      console.error('Erro ao gerar PDF do atestado:', error);
      toast.error(`Não foi possível gerar o PDF: ${error?.message || 'Erro desconhecido'}`);
    } finally {
      setIsGeneratingAttendanceCertificate(false);
    }
  }, [prepareAttendanceCertificateForExport]);

  const handlePrintAttendanceCertificate = useCallback(async (data: AttendanceCertificateFormData) => {
    try {
      setIsGeneratingAttendanceCertificate(true);
      const signedData = await prepareAttendanceCertificateForExport(data);
      setAttendanceCertificateData(signedData);
      await AttendanceCertificatePDFService.printPDF(signedData);
    } catch (error: any) {
      console.error('Erro ao imprimir atestado:', error);
      toast.error(`Não foi possível abrir a impressão: ${error?.message || 'Erro desconhecido'}`);
    } finally {
      setIsGeneratingAttendanceCertificate(false);
    }
  }, [prepareAttendanceCertificateForExport]);

  const loadProfessionals = useCallback(async () => {
    const requestedClientId = selectedClient?.clientId || user?.current_client_id || null;
    const isCurrentTenant = () => clinicTemporalContextRef.current.clientId === requestedClientId;

    try {
      if (!requestedClientId) {
        if (isCurrentTenant()) {
          setProfessionals([]);
          setCalendars([]);
        }
        return;
      }

      const userRows = await UserService.getAllUsersByClientId(requestedClientId);
      if (!isCurrentTenant()) return;
      const eligibleUsers = userRows.filter((item) =>
        item?.userId &&
        (item?.userRole === 'doctor' || item?.userRole === 'fisioterapeuta' || item?.userRole === 'interno')
      );

      const professionalUserIds = Array.from(
        new Set(eligibleUsers.map((item) => item.userId).filter(Boolean))
      );

      if (professionalUserIds.length === 0) {
        setProfessionals([]);
        setCalendars([]);
        return;
      }

      const { data: doctorsData, error: doctorsError } = await supabase
        .from('doctors')
        .select('doctorId, userId')
        .in('userId', professionalUserIds);

      if (doctorsError) {
        throw doctorsError;
      }
      if (!isCurrentTenant()) return;

      const doctorByUserId = new Map<string, string>();
      (doctorsData || []).forEach((doctor: any) => {
        if (doctor?.userId && doctor?.doctorId) {
          doctorByUserId.set(doctor.userId, doctor.doctorId);
        }
      });

      const list: ProfessionalOption[] = eligibleUsers
        .map((userRow: any) => {
          const userRole = userRow.userRole as AgendaAssignableRole | undefined;
          if (!userRole) return null;

          return buildProfessionalOption({
            doctorId: doctorByUserId.get(userRow.userId) ?? null,
            userId: userRow.userId,
            userRole,
            accessStartAt: userRow.accessStartAt ?? null,
            accessEndAt: userRow.accessEndAt ?? null,
            calendarName: userRow.userAssignedCalendarName,
            displayName: userRow.userDisplayName,
            agendaColor: userRow.agendaColor,
          });
        })
        .filter((item): item is ProfessionalOption => item !== null)
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

      setProfessionals(list);
      setCalendars(list.filter((professional) => professional.isAccessActive).map((professional) => professional.label));
    } catch (error: any) {
      if (!isCurrentTenant()) return;
      console.error('❌ Erro ao carregar profissionais:', error);
      setProfessionals([]);
      setCalendars([]);
      toast.error(`Erro ao carregar profissionais: ${error?.message || 'Erro desconhecido'}`);
    }
  }, [selectedClient?.clientId, user?.current_client_id]);

  const resolveProfessional = useCallback(async (inputValue: string): Promise<ProfessionalOption | null> => {
    const rawValue = (inputValue || '').trim();
    if (!rawValue) return null;

    const resolvedClientId = selectedClient?.clientId || user?.current_client_id || null;
    const normalizedValue = rawValue.toLowerCase();
    const localMatch = professionals.find((professional) =>
      professional.doctorId === rawValue ||
      professional.userId === rawValue ||
      professional.label.toLowerCase() === normalizedValue ||
      professional.calendarName.toLowerCase() === normalizedValue ||
      professional.displayName.toLowerCase() === normalizedValue
    );

    if (localMatch) {
      return localMatch;
    }

    if (!resolvedClientId) {
      return null;
    }

    if (UUID_REGEX.test(rawValue)) {
      const { data: userDataById, error: userDataByIdError } = await supabase
        .from('users')
        .select(`
          userId,
          userDisplayName,
          userAssignedCalendarName,
          user_clients!inner ( userRole, clientId, agendaColor )
        `)
        .eq('userId', rawValue)
        .eq('user_clients.clientId', resolvedClientId)
        .in('user_clients.userRole', ALLOWED_AGENDA_ROLES)
        .limit(1);

      if (userDataByIdError) {
        console.warn('⚠️ Erro ao buscar profissional por userId:', userDataByIdError);
      } else {
        const matchedUserById = userDataById?.[0];
        const userClientLink = relationRows(matchedUserById?.user_clients)[0] as {
          userRole?: AgendaAssignableRole;
          agendaColor?: string | null;
        } | undefined;

        if (
          matchedUserById?.userId &&
          (userClientLink?.userRole === 'doctor' || userClientLink?.userRole === 'fisioterapeuta' || userClientLink?.userRole === 'interno')
        ) {
          const { data: doctorByUserId, error: doctorByUserIdError } = await supabase
            .from('doctors')
            .select('doctorId')
            .eq('userId', matchedUserById.userId)
            .maybeSingle();

          if (doctorByUserIdError && doctorByUserIdError.code !== 'PGRST116') {
            console.warn('⚠️ Erro ao buscar doctorId pelo userId:', doctorByUserIdError);
            return null;
          }

          return buildProfessionalOption({
            doctorId: doctorByUserId?.doctorId ?? null,
            userId: matchedUserById.userId,
            userRole: userClientLink.userRole,
            calendarName: matchedUserById.userAssignedCalendarName,
            displayName: matchedUserById.userDisplayName,
            agendaColor: userClientLink.agendaColor,
          });
        }
      }

      const { data: doctorByDoctorId, error: doctorByDoctorIdError } = await supabase
        .from('doctors')
        .select('doctorId, userId')
        .eq('doctorId', rawValue)
        .maybeSingle();

      if (!doctorByDoctorIdError && doctorByDoctorId?.doctorId && doctorByDoctorId?.userId) {
        const { data: userData } = await supabase
          .from('users')
          .select(`
            userId,
            userDisplayName,
            userAssignedCalendarName,
            user_clients!inner ( userRole, clientId, agendaColor )
          `)
          .eq('userId', doctorByDoctorId.userId)
          .eq('user_clients.clientId', resolvedClientId)
          .in('user_clients.userRole', ALLOWED_AGENDA_ROLES)
          .maybeSingle();

        const userClientLink = relationRows((userData as any)?.user_clients)[0] as {
          userRole?: AgendaAssignableRole;
          agendaColor?: string | null;
        } | undefined;
        if (
          userData?.userId &&
          (userClientLink?.userRole === 'doctor' || userClientLink?.userRole === 'fisioterapeuta' || userClientLink?.userRole === 'interno')
        ) {
          return buildProfessionalOption({
            doctorId: doctorByDoctorId.doctorId,
            userId: doctorByDoctorId.userId,
            userRole: userClientLink.userRole,
            calendarName: (userData as any).userAssignedCalendarName,
            displayName: (userData as any).userDisplayName,
            agendaColor: userClientLink.agendaColor,
          });
        }
      }
    }

    const { data: matchedUsers, error: matchedUsersError } = await supabase
      .from('users')
      .select(`
        userId,
        userDisplayName,
        userAssignedCalendarName,
        user_clients!inner ( userRole, clientId, agendaColor )
      `)
      .eq('user_clients.clientId', resolvedClientId)
      .in('user_clients.userRole', ALLOWED_AGENDA_ROLES)
      .or(`userAssignedCalendarName.eq.${rawValue},userDisplayName.eq.${rawValue}`)
      .limit(1);

    if (matchedUsersError) {
      console.warn('⚠️ Erro ao tentar resolver profissional por nome:', matchedUsersError);
      return null;
    }

    const matchedUser = matchedUsers?.[0];
    if (!matchedUser?.userId) {
      return null;
    }

    const userClientLink = relationRows((matchedUser as any).user_clients)[0] as {
      userRole?: AgendaAssignableRole;
      agendaColor?: string | null;
    } | undefined;
    if (
      userClientLink?.userRole !== 'doctor' &&
      userClientLink?.userRole !== 'fisioterapeuta' &&
      userClientLink?.userRole !== 'interno'
    ) {
      return null;
    }

    const { data: doctorData, error: doctorDataError } = await supabase
      .from('doctors')
      .select('doctorId, userId')
      .eq('userId', matchedUser.userId)
      .maybeSingle();

    if (doctorDataError && doctorDataError.code !== 'PGRST116') {
      console.warn('⚠️ Erro ao buscar doctorId pelo userId:', doctorDataError);
      return null;
    }

    return buildProfessionalOption({
      doctorId: doctorData?.doctorId ?? null,
      userId: matchedUser.userId,
      userRole: userClientLink.userRole,
      calendarName: (matchedUser as any).userAssignedCalendarName,
      displayName: (matchedUser as any).userDisplayName,
      agendaColor: userClientLink.agendaColor,
    });
  }, [professionals, selectedClient?.clientId, user?.current_client_id]);

  // Carregar eventos do Supabase
  const loadEvents = useCallback(async () => {
    const requestId = ++agendaEventsRequestIdRef.current;
    const requestedClientId = activeClientId;
    const isCurrentRequest = () => (
      requestId === agendaEventsRequestIdRef.current
      && clinicTemporalContextRef.current.clientId === requestedClientId
    );

    try {
      setIsLoading(true);
      console.log('🔵 Iniciando carregamento da agenda...');

      if (!requestedClientId) {
        if (isCurrentRequest()) {
          setEvents([]);
          setIsLoading(false);
        }
        return;
      }

      const { data, error } = await supabase
        .from('appointments')
        .select(
          `
          *,
          users!appointments_userId_fkey (userAssignedCalendarName, userDisplayName)
          `
        )
        .eq('clientId', requestedClientId)
        .order('appointmentStartDate', { ascending: true });

      if (error) {
        throw error;
      }

      // Uma resposta do tenant anterior nunca pode repovoar a agenda depois
      // de uma troca de clínica enquanto a requisição estava em trânsito.
      if (!isCurrentRequest()) return;

      console.log('✅ Agenda carregada:', data.length, 'eventos');

      const list: AgendaEvent[] = data.map((appointment: AgendaAppointmentRow) =>
        parseAppointmentToAgendaEvent(appointment),
      );

      setEvents(list);
      setIsLoading(false);
      setLastSyncTime(new Date());
    } catch (error: any) {
      if (!isCurrentRequest()) return;
      console.error('❌ Erro crítico ao carregar eventos:', error);
      toast.error(`Erro crítico ao carregar agenda: ${error?.message || 'Erro desconhecido'}`);
      setIsLoading(false);
    }
  }, [activeClientId]);

  // Carregar eventos automaticamente ao montar o componente
  useEffect(() => {
    console.log("🔵 useEffect: Montando componente AgendaPage");
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    loadProfessionals();
  }, [loadProfessionals]);

  useEffect(() => {
    let isMounted = true;
    const loadAgendaServices = async () => {
      if (!activeClientId) {
        setAgendaServices([]);
        return;
      }

      setLoadingAgendaServices(true);
      const { data, error } = await supabase
        .from('financial_services')
        .select('serviceId, serviceName, serviceCode')
        .eq('clientId', activeClientId)
        .eq('isActive', true)
        .order('serviceName', { ascending: true });

      if (!isMounted) return;
      if (error) {
        console.error('Erro ao carregar serviços da agenda:', error);
        setAgendaServices([]);
        toast.error(`Erro ao carregar serviços: ${error.message}`);
      } else {
        setAgendaServices((data || []) as AgendaServiceOption[]);
      }
      setLoadingAgendaServices(false);
    };

    loadAgendaServices();
    return () => {
      isMounted = false;
    };
  }, [activeClientId]);

  // Carregar evoluções quando visualização em lista for ativada
  useEffect(() => {
    // Remove imediatamente qualquer estado do tenant anterior; as novas listas
    // só são publicadas pelas requisições que ainda pertencem ao tenant atual.
    setEvents([]);
    setPatients([]);
    setProfessionals([]);
    setCalendars([]);
    setSelectedEvent(null);
    setSelectedEvents(new Set());
    setAppointmentAuditTrail([]);
  }, [activeClientId]);

  useEffect(() => {
    let cancelled = false;
    const requestedClientId = activeClientId;
    const requestedAppointmentId = selectedEvent?.id || null;
    const canPublish = () => (
      !cancelled
      && clinicTemporalContextRef.current.clientId === requestedClientId
      && selectedEvent?.id === requestedAppointmentId
    );

    const loadAppointmentAuditTrail = async () => {
      if (!requestedAppointmentId || !showEventModal) {
        setAppointmentAuditTrail([]);
        return;
      }

      try {
        setLoadingAppointmentAuditTrail(true);
        const trail = await AppointmentStatusService.getResolutionAuditTrail(requestedAppointmentId);
        if (canPublish()) setAppointmentAuditTrail(trail);
      } catch (error) {
        if (!canPublish()) return;
        console.error('Erro ao carregar auditoria do agendamento:', error);
        setAppointmentAuditTrail([]);
      } finally {
        if (canPublish()) setLoadingAppointmentAuditTrail(false);
      }
    };

    loadAppointmentAuditTrail();

    return () => {
      cancelled = true;
    };
  }, [activeClientId, selectedEvent?.id, showEventModal]);

  useEffect(() => {
    if (activeCalendarFilterKey === 'all') {
      return;
    }

    const filterStillExists = calendarFilterOptions.some((item) => item.id === activeCalendarFilterKey);
    if (!filterStillExists) {
      setActiveCalendarFilterKey('all');
    }
  }, [activeCalendarFilterKey, calendarFilterOptions]);

  // Carregar pacientes
  useEffect(() => {
    const loadPatients = async () => {
      const requestedClientId = activeClientId;
      const isCurrentTenant = () => clinicTemporalContextRef.current.clientId === requestedClientId;

      try {
        setLoadingPatients(true);
        console.log("🔵 Carregando lista de pacientes...");

        if (!requestedClientId) {
          if (isCurrentTenant()) setPatients([]);
          return;
        }

        const { data: patientsData, error } = await supabase
          .from('patients')
          .select(`
            *,
            patient_clients!inner (
              clientId
            ),
            financial_profiles (
              financialProfileIsActive,
              clientId
            )
          `)
          .eq('patient_clients.clientId', requestedClientId)
          .order('patientFullName', { ascending: true }); // Assumindo 'patientFullName' como campo de ordenação

        if (error) {
          throw error;
        }
        if (!isCurrentTenant()) return;
        
        const formattedPatients = (patientsData || [])
          .filter((patient: any) => (patient.patientLifecycleStatus || 'active') !== 'archived')
          .map((patient: any) => {
            const lifecycleStatus = patient.patientLifecycleStatus || 'active';
            const financialProfiles = Array.isArray(patient.financial_profiles)
              ? patient.financial_profiles
              : patient.financial_profiles
                ? [patient.financial_profiles]
                : [];
            const currentFinancialProfile = requestedClientId
              ? financialProfiles.find((fp: any) => fp?.clientId === requestedClientId)
              : financialProfiles[0];
            const profileActive = typeof currentFinancialProfile?.financialProfileIsActive === 'boolean'
              ? currentFinancialProfile.financialProfileIsActive
              : (typeof patient.financialActive === 'boolean' ? patient.financialActive : true);

            return {
              id: patient.patientId,
              nome: (patient.patientFullName || '').split(' ')[0] || '',
              sobrenome: (patient.patientFullName || '').split(' ').slice(1).join(' ') || '',
              prontu_x00c1_rio: patient.patientMedicalRecordNumber || '',
              data_x0020_de_x0020_nascimento: patient.patientBirthDate || '',
              patientLifecycleStatus: lifecycleStatus,
              lifecycleStatus,
              financialProfile: {
                active: profileActive,
                hasPackage: patient.financialHasPackage ?? false,
                individualSessionPrice: patient.financialPricePerSession ?? 150,
              },
              ...patient,
            };
          });

        console.log("✅ Pacientes carregados:", formattedPatients.length);
        console.log("✅ Pacientes ativos:", formattedPatients.filter(p => p.financialProfile?.active).length);
        console.log("✅ Pacientes inativos:", formattedPatients.filter(p => !p.financialProfile?.active).length);
        setPatients(formattedPatients);
      } catch (error: any) {
        if (!isCurrentTenant()) return;
        console.error("❌ Erro ao carregar pacientes:", error);
        console.error("Detalhes:", { message: error?.message, code: error?.code });
        toast.error("Erro ao carregar lista de pacientes");
      } finally {
        if (isCurrentTenant()) setLoadingPatients(false);
      }
    };

    loadPatients();
  }, [activeClientId]);

  // Abrir modal ao selecionar um intervalo
  const handleSelectSlot = useCallback(
    ({ start, end }: SlotInfo) => {
      if (!canManageAgenda) {
        return;
      }

      if (!user) {
        toast.error('Você precisa estar logado para criar eventos');
        return;
      }
      let s = start as Date;
      let e = end as Date;

      if (currentView === Views.MONTH) {
        const clickedDay = moment.tz(start as Date, timeZone);
        s = clickedDay.clone().hour(AGENDA_START_HOUR).minute(0).second(0).millisecond(0).toDate();
        e = clickedDay.clone().hour(AGENDA_START_HOUR + 1).minute(0).second(0).millisecond(0).toDate();
      }

      setForm({
        subject: '',
        patientId: '',
        calendar_name: '',
        location: '',
        start: toDateTimeLocalInTimeZone(s, timeZone),
        end: toDateTimeLocalInTimeZone(e, timeZone),
        serviceId: '',
      });
      setUseCustomCalendarName(false);
      setShowCreateModal(true);
    },
    [canManageAgenda, currentView, openCreateModalForRange, timeZone, user]
  );

  // Salvar novo evento
  const handleCreateSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!canManageAgenda) {
      toast.error('O perfil Interno possui acesso somente para visualização da agenda.');
      return;
    }
    if (!user) return;

    let { subject, patientId, calendar_name, location, start, end, serviceId } = form;

    if (!patientId || patientId === '__outro__') {
      toast.error('Selecione um paciente cadastrado.');
      return;
    }

    if (!serviceId) {
      toast.error('Selecione o serviço agendado.');
      return;
    }

    const selectedPatient = patients.find((p: any) => p.id === patientId);
    const createBlockReason = selectedPatient ? getPatientNewCareBlockReason(selectedPatient) : 'lifecycle';
    if (!selectedPatient || createBlockReason) {
      toast.error(
        createBlockReason === 'clinical_inactive'
          ? 'Paciente está inativo assistencialmente (alta/parado/óbito) e não pode receber novos agendamentos.'
          : 'Paciente inativado/arquivado no cadastro não pode receber novos agendamentos.'
      );
      return;
    }

    if (useCustomCalendarName && !calendar_name.trim()) {
      toast.error('Informe o nome do calendário/profissional');
      return;
    }
    if (!useCustomCalendarName && calendars.length > 0 && !calendar_name) {
      toast.error('Selecione um calendário/profissional');
      return;
    }

    const normalizedLocation = normalizeAppointmentLocation(location);
    if (!normalizedLocation) {
      toast.error('Selecione um local válido para o atendimento.');
      return;
    }

    const dStart = parseDateTimeLocalInTimeZone(start, timeZone);
    const dEnd = parseDateTimeLocalInTimeZone(end, timeZone);
    if (isNaN(dStart.getTime()) || isNaN(dEnd.getTime())) {
      toast.error('Datas inválidas');
      return;
    }
    if (dEnd <= dStart) {
      toast.error('O fim precisa ser depois do início');
      return;
    }

    try {
      const { patientId, calendar_name, location, start, end } = form;
      const selectedPatientIds = createMode === 'group' ? groupPatientIds : [patientId];

      if (!calendar_name) {
        toast.error('Selecione um calendário existente.');
        return;
      }

      const normalizedLocation = normalizeAppointmentLocation(location);
      if (!normalizedLocation) {
        toast.error('Selecione um local válido para o atendimento.');
        return;
      }

      const selectedPatients = selectedPatientIds.map((selectedPatientId) =>
        patients.find((patient) => patient.id === selectedPatientId)
      );
      const missingPatient = selectedPatients.some((patient) => !patient);
      if (missingPatient) {
        toast.error(createMode === 'group'
          ? 'Selecione ao menos dois pacientes cadastrados para o grupo.'
          : 'Selecione um paciente cadastrado.');
        return;
      }

      const blockedPatient = selectedPatients.find((patient) => getPatientNewCareBlockReason(patient));
      if (blockedPatient) {
        const createBlockReason = getPatientNewCareBlockReason(blockedPatient);
        toast.error(
          createBlockReason === 'clinical_inactive'
            ? `${getPatientDisplayName(blockedPatient)} está inativo assistencialmente e não pode receber novos agendamentos.`
            : `${getPatientDisplayName(blockedPatient)} está inativado/arquivado e não pode receber novos agendamentos.`
        );
        return;
      }

      const conversionTimeZone = await confirmPersistedTimeZoneForWrite(
        createFormTimeZone,
        createFormClientId,
      );
      const dStart = createMode === 'series'
        ? undefined
        : parseDateTimeLocalInTimeZone(start, conversionTimeZone);
      const dEnd = createMode === 'series'
        ? undefined
        : parseDateTimeLocalInTimeZone(end, conversionTimeZone);
      const seriesRanges = createMode === 'series'
        ? buildSelectedSeriesRanges(conversionTimeZone)
        : undefined;
      const occurrences = buildAppointmentOccurrences({
        mode: createMode,
        patientIds: selectedPatientIds,
        start: dStart,
        end: dEnd,
        seriesRanges,
      });

      const resolvedProfessional = await resolveProfessional(calendar_name);
      const resolvedClientId = selectedClient?.clientId || user.current_client_id || null;

      if (!resolvedClientId) {
        toast.error('Cliente não identificado para criar o agendamento.');
        return;
      }

      if (!resolvedProfessional?.userId) {
        toast.error('Profissional inválido. Selecione um profissional cadastrado.');
        return;
      }

      const batchFingerprint = JSON.stringify({
        clientId: resolvedClientId,
        timeZone: conversionTimeZone,
        professionalUserId: resolvedProfessional.userId,
        location: normalizedLocation,
        mode: createMode,
        subject: subject.trim(),
        serviceId,
        serviceName: agendaServices.find((service) => service.serviceId === serviceId)?.serviceName || null,
        occurrences: occurrences.map((occurrence) => ({
          patientId: occurrence.patientId,
          start: occurrence.start.toISOString(),
          end: occurrence.end.toISOString(),
          index: occurrence.batchOccurrenceIndex,
        })),
      });

      let batchId: string | null = null;
      if (createMode !== 'single') {
        if (createBatchAttemptRef.current?.fingerprint !== batchFingerprint) {
          createBatchAttemptRef.current = {
            batchId: createAppointmentBatchId(),
            fingerprint: batchFingerprint,
          };
        }
        batchId = createBatchAttemptRef.current.batchId;
      }

      const appointmentCreatedAt = new Date().toISOString();
      const newAppointments = occurrences.map((occurrence) => {
        const patient = patients.find((item) => item.id === occurrence.patientId);
        const appointment = {
          clientId: resolvedClientId,
          doctorId: resolvedProfessional.doctorId ?? null,
          patientId: occurrence.patientId,
          appointmentTitle: subject.trim() || getPatientDisplayName(patient),
          appointmentServiceId: serviceId,
          appointmentServiceNameSnapshot: agendaServices.find((service) => service.serviceId === serviceId)?.serviceName || null,
          appointmentStartDate: occurrence.start.toISOString(),
          appointmentEndDate: occurrence.end.toISOString(),
          appointmentTimeZone: conversionTimeZone,
          userId: resolvedProfessional.userId,
          appointmentLocation: normalizedLocation,
          appointmentStatus: 'scheduled',
          appointmentCreatedAt,
          appointmentDurationMinutes: Math.round(
            (occurrence.end.getTime() - occurrence.start.getTime()) / (1000 * 60)
          ),
          ...(batchId ? {
            appointmentBatchId: batchId,
            appointmentBatchType: occurrence.batchType,
            appointmentBatchOccurrenceIndex: occurrence.batchOccurrenceIndex,
          } : {}),
        };

        return appointment;
      });

      await confirmPersistedTimeZoneForWrite(conversionTimeZone, createFormClientId);
      const { error: insertError } = await supabase.from('appointments').insert(newAppointments);

      if (insertError) {
        if (insertError.code !== '23505' || !batchId) {
          throw insertError;
        }

        // Se a resposta da primeira tentativa se perdeu, confirme que o lote inteiro já existe.
        const { data: existingBatch, error: recoveryError } = await supabase
          .from('appointments')
          .select('patientId, appointmentStartDate, appointmentEndDate, appointmentTimeZone, appointmentStatus, appointmentBatchType, appointmentBatchOccurrenceIndex')
          .eq('clientId', resolvedClientId)
          .eq('appointmentBatchId', batchId);

        const isCompleteExistingBatch = !recoveryError
          && matchesCompleteAppointmentBatch(existingBatch || [], occurrences, conversionTimeZone);

        if (!isCompleteExistingBatch) {
          throw insertError;
        }
      }

      await loadEvents();
      setCurrentDate(occurrences[0].start);
      createBatchAttemptRef.current = null;

      toast.success(
        occurrences.length === 1
          ? 'Agendamento criado com sucesso!'
          : `${occurrences.length} agendamentos criados com sucesso!`
      );
      closeCreateModal();
    } catch (error: unknown) {
      console.error('Erro ao criar agendamento(s):', error);
      const errorMessage = error instanceof Error
        ? error.message
        : typeof error === 'object' && error && 'message' in error
          ? String(error.message)
          : 'Erro desconhecido';
      toast.error(`Erro ao criar agendamento(s): ${errorMessage}`);
    } finally {
      createSubmissionLockRef.current = false;
      setIsCreatingAppointments(false);
    }
  };

  // Clique em evento existente
  const handleSelectEvent = useCallback((event: AgendaEvent) => {
    if (selectionMode) {
      toggleEventSelection(event.id);
    } else {
      setSelectedEvent(event);
      setShowEventModal(true);
    }
  }, [selectionMode]);

  const isProtectedAppointment = (event?: AgendaEvent | null) => {
    if (!event) return false;
    return event.status === 'completed' || Boolean(event.evolutionId);
  };

  const canDeleteAppointment = (event?: AgendaEvent | null) => {
    if (!event) return false;
    return resolveAgendaEventUiStatus(event) === 'scheduled' && !isProtectedAppointment(event);
  };

  // Função para excluir evento
  const handleDeleteEvent = async (eventId: string) => {
    if (!canManageAgenda) {
      toast.error('O perfil Interno possui acesso somente para visualização da agenda.');
      return;
    }

    if (!user) {
      toast.error("Você precisa estar logado para excluir eventos");
      return;
    }

    if (!activeClientId) {
      toast.error("Tenant não identificado para excluir o agendamento");
      return;
    }

    const targetEvent = events.find((event) => event.id === eventId);
    if (!canDeleteAppointment(targetEvent)) {
      toast.error('Exclusão permitida apenas para agendamentos com status "Agendado".');
      setShowDeleteConfirm(null);
      return;
    }

    try {
      await IntegrationService.assertAppointmentEditable(eventId, activeClientId);
    } catch (error: any) {
      toast.error(error?.message || 'Este agendamento já está bloqueado para alteração.');
      setShowDeleteConfirm(null);
      return;
    }

    try {
      setIsDeleting(true);
      console.log("🗑️ Iniciando exclusão do evento:", eventId);

      const { error } = await supabase
        .from("appointments")
        .delete()
        .eq("appointmentId", eventId)
        .eq("clientId", activeClientId);

      if (error) {
        throw error;
      }

      console.log("✅ Evento deletado do Supabase");

      await new Promise(resolve => setTimeout(resolve, 500)); // Simular atraso para UX

      await loadEvents();

      toast.success("Evento excluído com sucesso!");
      setShowDeleteConfirm(null);
      setShowEventModal(false);
      setSelectedEvent(null);
    } catch (error: any) {
      console.error("❌ Erro ao excluir evento:", error);
      toast.error(`Erro ao excluir evento: ${error?.message || "Erro desconhecido"}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // 🆕 Funções de edição de agendamentos
  const handleEditClick = (event: AgendaEvent) => {
    if (!canManageAgenda) {
      toast.error('O perfil Interno possui acesso somente para visualização da agenda.');
      return;
    }
    if (!isClinicTimeZoneReady) {
      toast.error('Aguarde a confirmação do fuso horário da clínica antes de editar.');
      return;
    }
    console.log('✏️ Iniciando edição do evento:', event);

    const eventStatus = resolveAgendaEventUiStatus(event);
    if (eventStatus !== 'scheduled') {
      toast.warning(getEditSectionMessage(eventStatus));
      return;
    }

    // Preparar formulário de edição
    setEditingEvent(event);
    setEditFormTimeZone(timeZone);
    setEditFormClientId(activeClientId);
    setEditForm({
      subject: event.title,
      patientId: event.patientId || '',
      calendar_name: event.userId || event.doctorId || event.calendar_name || '',
      location: event.location || '',
      start: moment(event.start).format('YYYY-MM-DDTHH:mm'),
      end: moment(event.end).format('YYYY-MM-DDTHH:mm'),
      serviceId: event.serviceId || '',
    });

    if (event.locationIssue) {
      toast.warning(`Este agendamento está com local inconsistente: ${event.locationIssue}. Escolha "Clínica" ou "Domicílio" para salvar.`);
    }
    
    setShowEditModal(true);
    setShowEventModal(false);
  };

  const handleEditSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();

    if (!user) {
      toast.error('Você precisa estar logado para editar eventos');
      return;
    }

    if (!editingEvent) {
      toast.error('Nenhum evento selecionado para edição');
      return;
    }

    if (!activeClientId) {
      toast.error('Tenant não identificado para editar o agendamento');
      return;
    }
    if (!isClinicTimeZoneReady) {
      toast.error('Não foi possível confirmar o fuso horário da clínica. Recarregue a página antes de editar.');
      return;
    }

    try {
      console.log('💾 Salvando alterações do evento:', editingEvent.id);

      await IntegrationService.assertAppointmentEditable(editingEvent.id, activeClientId);
      
      const conversionTimeZone = await confirmPersistedTimeZoneForWrite(
        editFormTimeZone,
        editFormClientId,
      );
      const startDate = parseDateTimeLocalInTimeZone(editForm.start, conversionTimeZone);
      const endDate = parseDateTimeLocalInTimeZone(editForm.end, conversionTimeZone);

      // Validações
      if (endDate <= startDate) {
        toast.error('A data/hora de término deve ser posterior à de início');
        return;
      }

      if (!editForm.patientId || editForm.patientId === '__outro__') {
        toast.error('Selecione um paciente cadastrado.');
        return;
      }

      if (!editForm.serviceId) {
        toast.error('Selecione o serviço agendado.');
        return;
      }

      const selectedPatient = patients.find((p: any) => p.id === editForm.patientId);
      const editBlockReason = selectedPatient ? getPatientNewCareBlockReason(selectedPatient) : 'lifecycle';
      if (!selectedPatient || editBlockReason) {
        toast.error(
          editBlockReason === 'clinical_inactive'
            ? 'Paciente está inativo assistencialmente (alta/parado/óbito) e não pode receber novos agendamentos.'
            : 'Paciente inativado/arquivado no cadastro não pode receber novos agendamentos.'
        );
        return;
      }

      const resolvedProfessional = await resolveProfessional(editForm.calendar_name);
      if (!resolvedProfessional?.userId) {
        toast.error('Profissional inválido. Selecione um profissional cadastrado.');
        return;
      }

      const normalizedLocation = normalizeAppointmentLocation(editForm.location);
      if (!normalizedLocation) {
        toast.error('Selecione um local válido para o atendimento.');
        return;
      }

      const updateData = {
        appointmentTitle: editForm.subject,
        appointmentServiceId: editForm.serviceId,
        appointmentServiceNameSnapshot: agendaServices.find((service) => service.serviceId === editForm.serviceId)?.serviceName || null,
        patientId: editForm.patientId || null,
        doctorId: resolvedProfessional.doctorId ?? null,
        userId: resolvedProfessional.userId,
        appointmentLocation: normalizedLocation,
        appointmentStartDate: startDate.toISOString(),
        appointmentEndDate: endDate.toISOString(),
        appointmentTimeZone: conversionTimeZone,
        appointmentUpdatedAt: new Date().toISOString(),
      };

      await confirmPersistedTimeZoneForWrite(conversionTimeZone, editFormClientId);
      const { error } = await supabase
        .from("appointments")
        .update(updateData)
        .eq("appointmentId", editingEvent.id)
        .eq("clientId", activeClientId);

      if (error) {
        throw error;
      }

      console.log('✅ Evento atualizado no Supabase');

      await loadEvents();
      setCurrentDate(startDate);

      toast.success('Evento atualizado com sucesso!');
      setShowEditModal(false);
      setEditingEvent(null);
      setEditForm({
        subject: '',
        patientId: '',
        calendar_name: '',
        location: '',
        start: '',
        end: '',
        serviceId: '',
      });
    } catch (error: any) {
      console.error('❌ Erro ao atualizar evento:', error);
      toast.error(`Erro ao atualizar evento: ${error?.message || "Erro desconhecido"}`);
    }
  };

  const handleUpdateStatus = async (appointmentId: string, status: 'completed' | 'no_show' | 'cancelled') => {
    if (isUpdating) return; // 🟢 Previne múltiplos cliques
    if (!canManageAgenda) {
      toast.error('O perfil Interno possui acesso somente para visualização da agenda.');
      return;
    }
    try {
      if (!user) {
        toast.error("Você precisa estar logado para atualizar o status.");
        return;
      }

      await IntegrationService.assertAppointmentEditable(appointmentId, activeClientId);
      
      setIsUpdating(true); // 🟢 Ativa o bloqueio da UI

      if (status === 'completed') {
        if (!activeClientId) {
          throw new Error('Nenhum cliente ativo foi selecionado para concluir o atendimento.');
        }

        try {
          const appointmentForCompletion = events.find((event) => event.id === appointmentId);
          const selectedService = agendaServices.find((service) => service.serviceId === appointmentForCompletion?.serviceId);
          await AppointmentStatusService.repairPeriodCreditLimits(appointmentId, activeClientId);
          const preview = await AppointmentStatusService.previewFinancialOrigin(
            appointmentId,
            activeClientId,
            selectedService?.serviceName || appointmentForCompletion?.serviceName || null,
          );
          const initialId = preview.recommendedId || preview.options[0]?.id || '';
          const initialOption = preview.options.find((option) => option.id === initialId);
          setFinancialOriginPreview(preview);
          setSelectedFinancialOriginId(initialId);
          setSelectedFinancialOriginKind(initialOption?.kind || preview.options[0]?.kind || 'pacote');
          setPendingFinancialAppointmentId(appointmentId);
          setShowEventModal(false);
          setShowFinancialOriginModal(true);
          return;
        } catch (previewError: any) {
          const previewMessage = String(previewError?.message || '');
          const isMissingPreviewRpc = /function .*financial_a2_preview_appointment_origins|schema cache|does not exist/i.test(previewMessage);
          if (!isMissingPreviewRpc) {
            throw previewError;
          }
          console.warn('RPC de prévia A2 ainda não aplicada; concluindo com compatibilidade automática.', previewMessage);
          await AppointmentStatusService.completeAppointment(appointmentId, user.userId);
        }
      } else if (status === 'no_show') {
        const appointment = events.find(e => e.id === appointmentId);
        if (appointment && appointment.patientId && activeClientId) {
          const preview = await AppointmentStatusService.previewAbsenceDecision(activeClientId, appointmentId);
          setAbsenceDecisionPreview(preview);
          setAbsenceDecisionAppointmentId(appointmentId);
          setAbsenceIdempotencyKey(crypto.randomUUID());
          setShowEventModal(false);
          setIsUpdating(false);
          return;
        } else {
          toast.error("Paciente ou tenant não encontrado para registrar a falta.");
          setIsUpdating(false);
          return;
        }
      } else if (status === 'cancelled') {
        await AppointmentStatusService.resolveAsCancelled(appointmentId, user.userId);
      }

      await loadEvents();
      toast.success("Status do agendamento atualizado com sucesso!");
      
      setShowEventModal(false);
      setSelectedEvent(null);

    } catch (error: any) {
      console.error('Erro ao atualizar status do agendamento:', error);
      const message = String(error?.message || 'Erro desconhecido');
      if (message.includes('MISSING_INDIVIDUAL_SESSION_PRICE')) {
        toast.error(
          'Não foi possível concluir: o paciente não possui preço avulso configurado e não há pacote válido com saldo.',
        );
      } else {
        toast.error(`Erro ao atualizar status: ${message}`);
      }
    } finally {
      setIsUpdating(false); // 🟢 Libera a UI no final
    }
  };

  const openStatusResolutionModal = (event: AgendaEvent | null, action: 'no_show' | 'cancelled') => {
    if (!event) {
      toast.error('Nenhum agendamento selecionado.');
      return;
    }
    void handleUpdateStatus(event.id, action);
  };

  const handleCancelFinancialOriginSelection = () => {
    setShowFinancialOriginModal(false);
    setFinancialOriginPreview(null);
    setSelectedFinancialOriginId('');
    setSelectedFinancialOriginKind('pacote');
    setPendingFinancialAppointmentId('');
  };

  const handleConfirmFinancialOriginSelection = async () => {
    if (isUpdating || !user || !financialOriginPreview || !pendingFinancialAppointmentId) return;

    const selectedOption = financialOriginPreview.options.find(
      (option) => option.id === selectedFinancialOriginId,
    );
    if (!selectedOption) {
      toast.error('Selecione como este atendimento será coberto antes de concluir.');
      return;
    }

    try {
      setIsUpdating(true);
      const completionResult = await AppointmentStatusService.completeAppointment(
        pendingFinancialAppointmentId,
        user.userId,
        undefined,
        {
          origin: selectedOption.kind === 'pacote' ? 'pacote' : 'avulsa',
          serviceType: selectedOption.serviceType ?? null,
          contractPricingRuleId: selectedOption.contractPricingRuleId ?? null,
          financialPackageId: selectedOption.packageId ?? null,
        },
      );
      await loadEvents();
      if (completionResult?.origin === 'avulsa' && completionResult.financialInvoiceId) {
        toast.success(
          completionResult.grouped
            ? 'Atendimento concluído e cobrança avulsa adicionada à fatura mensal.'
            : 'Atendimento concluído e fatura avulsa gerada automaticamente.',
        );
      } else {
        toast.success('Atendimento concluído e origem financeira registrada com sucesso!');
      }
      handleCancelFinancialOriginSelection();
      setSelectedEvent(null);
    } catch (error: any) {
      console.error('Erro ao concluir atendimento com origem financeira:', error);
      const message = String(error?.message || 'Erro desconhecido');
      if (message.includes('MISSING_INDIVIDUAL_SESSION_PRICE')) {
        toast.error('Não foi possível concluir: não há preço avulso configurado e não há pacote válido com saldo.');
      } else {
        toast.error(`Erro ao registrar a origem financeira: ${message}`);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleReopenNoEvaluated = async (event: AgendaEvent) => {
    if (isUpdating) return;
    if (!canManageAgenda) {
      toast.error('O perfil Interno possui acesso somente para visualização da agenda.');
      return;
    }
    if (!user) {
      toast.error('Você precisa estar logado para reabrir o agendamento.');
      return;
    }
    if (!activeClientId) {
      toast.error('Tenant não identificado para reabrir o agendamento.');
      return;
    }
    try {
      setIsUpdating(true);
      const preview = await AppointmentStatusService.previewReopenNoEvaluated(event.id, {
        clientId: activeClientId,
      });

      if (!preview.canReopen) {
        throw new Error(preview.blockingReasons[0] || 'Não foi possível reabrir o agendamento.');
      }

      if (preview.requiresSelection) {
        setReopenPreview(preview);
        setSelectedReopenCandidateId('');
        setShowEventModal(false);
        setShowReopenSelectionModal(true);
        return;
      }

      await AppointmentStatusService.confirmReopenNoEvaluated(event.id, {
        clientId: activeClientId,
        actor: {
          userId: user.userId,
          userRole: activeRole || user.userRole,
          displayName: user.userDisplayName,
          email: user.userEmail,
          clientId: activeClientId,
        },
        selectedCandidateId: preview.candidates[0]?.id ?? null,
        expectedAppointmentUpdatedAt: preview.appointment?.appointmentUpdatedAt ?? null,
      });

      await loadEvents();
      toast.success('Agendamento voltou para "Agendado" com estorno financeiro aplicado.');
      setShowEventModal(false);
      setSelectedEvent(null);
    } catch (error: any) {
      console.error('Erro ao reabrir agendamento:', error);
      toast.error(`Erro ao reabrir agendamento: ${error?.message || 'Erro desconhecido'}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleConfirmReopenSelection = async () => {
    if (!reopenPreview?.appointment) {
      toast.error('Dados de reabertura não encontrados. Tente novamente.');
      return;
    }
    if (!selectedReopenCandidateId) {
      toast.error('Selecione o item financeiro que será estornado.');
      return;
    }
    if (!user || !activeClientId) {
      toast.error('Sessão inválida para concluir a reabertura.');
      return;
    }
    try {
      setIsUpdating(true);
      await AppointmentStatusService.confirmReopenNoEvaluated(reopenPreview.appointment.appointmentId, {
        clientId: activeClientId,
        actor: {
          userId: user.userId,
          userRole: activeRole || user.userRole,
          displayName: user.userDisplayName,
          email: user.userEmail,
          clientId: activeClientId,
        },
        selectedCandidateId: selectedReopenCandidateId,
        expectedAppointmentUpdatedAt: reopenPreview.appointment.appointmentUpdatedAt ?? null,
      });

      await loadEvents();
      toast.success('Agendamento voltou para "Agendado" com estorno financeiro aplicado.');
      setShowReopenSelectionModal(false);
      setReopenPreview(null);
      setSelectedReopenCandidateId('');
      setSelectedEvent(null);
    } catch (error: any) {
      console.error('Erro ao confirmar reabertura:', error);
      toast.error(`Erro ao confirmar reabertura: ${error?.message || 'Erro desconhecido'}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelReopenSelection = () => {
    setShowReopenSelectionModal(false);
    setReopenPreview(null);
    setSelectedReopenCandidateId('');
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingEvent(null);
    setEditForm({
      subject: '',
      patientId: '',
      calendar_name: '',
      location: '',
      start: '',
      end: '',
      serviceId: '',
    });
  };

  // Funções de seleção múltipla
  const toggleEventSelection = (eventId: string) => {
    if (!canManageAgenda) {
      return;
    }

    setSelectedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  const selectAllEvents = () => {
    if (!canManageAgenda) {
      return;
    }

    if (selectedEvents.size === filteredEvents.length) {
      setSelectedEvents(new Set());
    } else {
      setSelectedEvents(new Set(filteredEvents.map(e => e.id)));
    }
  };

  const selectAllVisibleEvents = () => {
    if (!canManageAgenda) {
      return;
    }

    const visibleEvents = events.filter(event => {
      if (!matchesCalendarFilter(event)) {
        return false;
      }
      const eventDate = moment.tz(event.start, timeZone);
      const viewStart = moment.tz(currentDate, timeZone).startOf(currentView === Views.MONTH ? 'month' : currentView === Views.WEEK ? 'week' : 'day');
      const viewEnd = moment.tz(currentDate, timeZone).endOf(currentView === Views.MONTH ? 'month' : currentView === Views.WEEK ? 'week' : 'day');
      return eventDate.isBetween(viewStart, viewEnd, null, '[]');
    });

    if (selectedEvents.size === visibleEvents.length && visibleEvents.length > 0) {
      setSelectedEvents(new Set());
    } else {
      setSelectedEvents(new Set(visibleEvents.map(e => e.id)));
    }
  };

  // Função para excluir eventos em bloco
  const handleBulkDeleteEvents = async () => {
    if (!canManageAgenda) {
      toast.error('O perfil Interno possui acesso somente para visualização da agenda.');
      return;
    }

    if (!user || selectedEvents.size === 0) {
      toast.error("Nenhum evento selecionado");
      return;
    }

    if (!activeClientId) {
      toast.error("Tenant não identificado para exclusão em lote");
      return;
    }

    const selectedEventList = events.filter((event) => selectedEvents.has(event.id));
    const blockedEvents = selectedEventList.filter((event) => isProtectedAppointment(event));

    if (blockedEvents.length > 0) {
      toast.error(
        `Exclusão bloqueada: ${blockedEvents.length} agendamento(s) concluído(s) ou com prontuário/evolução selecionado(s).`
      );
      return;
    }

    try {
      setIsDeleting(true);
      console.log("🗑️ Iniciando exclusão em lote de", selectedEvents.size, "eventos");

      const eventIdsArray = Array.from(selectedEvents);

      const { error } = await supabase
        .from("appointments")
        .delete()
        .in("appointmentId", eventIdsArray)
        .eq("clientId", activeClientId);

      if (error) {
        throw error;
      }

      console.log("✅ Eventos deletados do Supabase");

      await new Promise(resolve => setTimeout(resolve, 500)); // Simular atraso para UX

      await loadEvents();

      toast.success(`${selectedEvents.size} evento(s) excluído(s) com sucesso!`);
      setSelectedEvents(new Set());
      setShowBulkDeleteConfirm(false);
    } catch (error: any) {
      console.error("❌ Erro ao excluir eventos:", error);
      toast.error(`Erro ao excluir eventos em bloco: ${error?.message || "Erro desconhecido"}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Mensagens/formatos
  const messages = {
    allDay: 'Dia inteiro',
    previous: 'Anterior',
    next: 'Próximo',
    today: 'Hoje',
    month: 'Mês',
    week: 'Semana',
    day: 'Dia',
    agenda: 'Agendamentos',
    date: 'Data',
    time: 'Hora',
    event: 'Evento',
    noEventsInRange: 'Não há eventos neste período',
    showMore: (total: number) => `+${total} mais`,
  };

  const formats = {
    monthHeaderFormat: (date: Date) => formatShortToken(moment.tz(date, timeZone).format('MMMM YYYY')),
    dayHeaderFormat: (date: Date) => `${formatShortToken(moment.tz(date, timeZone).format('dddd'))}, ${moment.tz(date, timeZone).format('DD')} ${formatCompactMonth(date, timeZone)} ${moment.tz(date, timeZone).format('YYYY')}`,
    dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }) =>
      `${moment.tz(start, timeZone).format('DD')} ${formatCompactMonth(start, timeZone)} - ${moment.tz(end, timeZone).format('DD')} ${formatCompactMonth(end, timeZone)} ${moment.tz(end, timeZone).format('YYYY')}`,
    agendaHeaderFormat: ({ start, end }: { start: Date; end: Date }) =>
      `${moment.tz(start, timeZone).format('DD')} ${formatCompactMonth(start, timeZone)} - ${moment.tz(end, timeZone).format('DD')} ${formatCompactMonth(end, timeZone)} ${moment.tz(end, timeZone).format('YYYY')}`,
    selectRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
      `${moment.tz(start, timeZone).format('DD/MM HH:mm')} - ${moment.tz(end, timeZone).format('DD/MM HH:mm')}`,
    agendaDateFormat: (date: Date) => `${moment.tz(date, timeZone).format('DD/MM')} (${moment.tz(date, timeZone).format('ddd')})`,
    agendaTimeFormat: (date: Date) => moment.tz(date, timeZone).format('HH:mm'),
    agendaTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
      `${moment.tz(start, timeZone).format('HH:mm')} - ${moment.tz(end, timeZone).format('HH:mm')}`,
    dayFormat: (date: Date) => formatWeekHeaderLabel(date, timeZone),
    weekdayFormat: (date: Date) => formatShortToken(moment.tz(date, timeZone).format('ddd')),
    timeGutterFormat: (date: Date) => moment.tz(date, timeZone).format('HH:mm'),
  };

  const eventStyleGetter = (event: AgendaEvent) => {
    const isSelected = selectedEvents.has(event.id);
    const calendarAccent = getEventCalendarAccentColor(event);
    const style: any = {
      background: '#ffffff',
      borderRadius: '8px',
      opacity: 1,
      color: '#0f172a',
      border: isSelected ? '2px solid rgba(30, 64, 175, 0.95)' : '1px solid rgba(226, 232, 240, 0.95)',
      display: 'block',
      cursor: 'pointer',
      boxShadow: isSelected
        ? `0 14px 28px ${hexToRgba('#1e40af', 0.18)}`
        : '0 4px 14px rgba(15, 23, 42, 0.06)',
      overflow: 'hidden',
      '--agenda-calendar-color': calendarAccent,
    };

    return {
      style,
    };
  };

  const selectedEventsList = events.filter((event) => selectedEvents.has(event.id));
  const selectedProtectedEventsCount = selectedEventsList.filter((event) => isProtectedAppointment(event)).length;
  const selectedEventForDeleteModal = showDeleteConfirm
    ? events.find((event) => event.id === showDeleteConfirm) || selectedEvent
    : selectedEvent;
  const isDeleteModalEventBlocked = !canDeleteAppointment(selectedEventForDeleteModal);
  const agendaSelectedDay = moment.tz(currentDate, timeZone).startOf('day');
  const agendaDayEvents = filteredCalendarEvents
    .filter((event) => moment.tz(event.start, timeZone).isSame(agendaSelectedDay, 'day'))
    .sort((left, right) => left.start.getTime() - right.start.getTime());
  const agendaMonthStart = agendaSelectedDay.clone().startOf('month').startOf('week');
  const agendaMonthEnd = agendaSelectedDay.clone().endOf('month').endOf('week');
  const agendaMonthDays: Date[] = [];
  const agendaMonthCursor = agendaMonthStart.clone();
  while (agendaMonthCursor.isSameOrBefore(agendaMonthEnd, 'day')) {
    agendaMonthDays.push(agendaMonthCursor.toDate());
    agendaMonthCursor.add(1, 'day');
  }

  const getAgendaDayEventCount = (date: Date): number =>
    filteredCalendarEvents.filter((event) => moment.tz(event.start, timeZone).isSame(moment.tz(date, timeZone), 'day')).length;

  const getAgendaPrimaryStatusMeta = (event: AgendaEvent) => {
    const resolvedStatus = resolveAgendaEventUiStatus(event);

    if (resolvedStatus === 'cancelled') {
      return {
        icon: XCircle,
        label: 'Cancelado',
        iconClassName: 'text-slate-400',
        rowClassName: 'border-slate-200 bg-slate-50 text-slate-500',
      };
    }

    if (resolvedStatus === 'no_show') {
      return {
        icon: AlertTriangle,
        label: 'Falta',
        iconClassName: 'text-red-500',
        rowClassName: 'border-slate-200 bg-white',
      };
    }

    if (resolvedStatus === 'no_evaluated') {
      return {
        icon: AlertTriangle,
        label: 'Sem evolução',
        iconClassName: 'text-amber-500',
        rowClassName: 'border-slate-200 bg-white',
      };
    }

    if (resolvedStatus === 'no_billed') {
      return {
        icon: DollarSign,
        label: 'A faturar',
        iconClassName: 'text-violet-500',
        rowClassName: 'border-slate-200 bg-white',
      };
    }

    if (resolvedStatus === 'completed') {
      return {
        icon: CheckCircle,
        label: 'Concluído',
        iconClassName: 'text-emerald-500',
        rowClassName: 'border-slate-200 bg-white',
      };
    }

    return {
      icon: Clock,
      label: 'Agendado',
      iconClassName: 'text-slate-400',
      rowClassName: 'border-slate-200 bg-white',
    };
  };

  const getAgendaStatusTooltip = (event: AgendaEvent): string => {
    const primaryStatus = getAgendaPrimaryStatusMeta(event);
    const tooltipItems = [
      `Status principal: ${primaryStatus.label}`,
      hasEvolution(event) ? 'Evolução registrada' : 'Sem evolução registrada',
      event.isBilled ? 'Faturado' : 'Não faturado',
      event.location ? `Local: ${event.location}` : '',
      `Horário: ${moment.tz(event.start, timeZone).format('HH:mm')} - ${moment.tz(event.end, timeZone).format('HH:mm')}`,
    ];

    return tooltipItems.filter(Boolean).join(' • ');
  };

  const navigateAgendaDay = (action: 'PREV' | 'NEXT' | 'TODAY') => {
    if (action === 'TODAY') {
      setCurrentDate(new Date());
      return;
    }

    const nextDate = moment.tz(currentDate, timeZone)
      .add(action === 'NEXT' ? 1 : -1, 'day')
      .toDate();

    setCurrentDate(nextDate);
  };

  const renderAgendaDayView = () => {
    return (
      <>
        <AgendaToolbar
          date={currentDate}
          view={currentView}
          views={AGENDA_VIEW_ORDER}
          onNavigate={navigateAgendaDay}
          onView={setCurrentView}
          timeZone={timeZone}
        />

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
          <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    {formatToolbarRangeLabel(currentDate, Views.AGENDA, timeZone)}
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    {agendaDayEvents.length} agendamento(s) neste dia
                  </p>
                </div>

                {canManageAgenda && (
                  <ActionButton
                    type="button"
                    onClick={() => {
                      const startDate = agendaSelectedDay.clone().hour(AGENDA_START_HOUR).minute(0).second(0).millisecond(0).toDate();
                      const endDate = agendaSelectedDay.clone().hour(AGENDA_START_HOUR + 1).minute(0).second(0).millisecond(0).toDate();
                      openCreateModalForRange(startDate, endDate);
                    }}
                    variant="primary"
                    size="lg"
                  >
                    <Plus className="h-4 w-4" />
                    Novo agendamento
                  </ActionButton>
                )}
              </div>
            </div>

            {agendaDayEvents.length === 0 ? (
              <div className="flex min-h-[320px] flex-col items-center justify-center px-6 py-10 text-center">
                <CalendarIcon className="mb-3 h-10 w-10 text-slate-300" />
                <p className="text-base font-medium text-slate-700">Nenhum agendamento neste dia</p>
                <p className="mt-1 text-sm text-slate-500">
                  Use o mini calendário para navegar entre datas com ocupação.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {agendaDayEvents.map((event) => {
                  const statusMeta = getAgendaPrimaryStatusMeta(event);
                  const StatusIcon = statusMeta.icon;
                  const isSelected = selectedEvents.has(event.id);
                  const calendarAccent = getEventCalendarAccentColor(event);
                  const rowSubtitle = [
                    getEventCalendarLabel(event),
                    event.location || '',
                  ].filter(Boolean).join(' • ');

                  return (
                    <button
                      key={event.id}
                      type="button"
                      onClick={() => handleSelectEvent(event)}
                      title={getAgendaStatusTooltip(event)}
                      className={`grid w-full grid-cols-[72px_minmax(0,1fr)] items-stretch text-left transition hover:bg-slate-50 ${isSelected ? 'bg-blue-50/70' : 'bg-white'}`}
                    >
                      <div className="flex items-start justify-end px-3 py-4">
                        <span className="text-[18px] font-semibold leading-none tracking-tight text-slate-900">
                          {moment.tz(event.start, timeZone).format('HH:mm')}
                        </span>
                      </div>

                      <div className="px-4 py-3">
                        <div className={`flex items-start gap-3 rounded-2xl border px-3 py-3 shadow-sm ${statusMeta.rowClassName}`}>
                          <div className="pt-0.5" title={getAgendaStatusTooltip(event)}>
                            <StatusIcon className={`h-4 w-4 ${statusMeta.iconClassName}`} />
                          </div>

                          <div className="min-w-0 flex-1">
                            <p className={`truncate text-[15px] font-semibold leading-tight ${resolveAgendaEventUiStatus(event) === 'cancelled' ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                              {event.title || 'Paciente sem nome'}
                            </p>
                            {rowSubtitle && (
                              <div className="mt-1 flex items-center gap-2 text-sm text-slate-500">
                                <span
                                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                                  style={{ backgroundColor: calendarAccent }}
                                />
                                <p className="truncate">{rowSubtitle}</p>
                              </div>
                            )}
                          </div>

                          {selectionMode && (
                            <div className="pt-0.5">
                              <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full border text-[11px] font-semibold ${
                                isSelected
                                  ? 'border-blue-600 bg-blue-600 text-white'
                                  : 'border-slate-300 bg-white text-slate-400'
                              }`}>
                                {isSelected ? '✓' : ''}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <aside className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setCurrentDate(agendaSelectedDay.clone().subtract(1, 'month').toDate())}
                className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Mês anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="text-center">
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-400">Navegação</p>
                <p className="mt-1 text-lg font-semibold text-slate-900">
                  {formatShortToken(agendaSelectedDay.format('MMMM YYYY'))}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCurrentDate(agendaSelectedDay.clone().add(1, 'month').toDate())}
                className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Próximo mês"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-5 grid grid-cols-7 gap-2 text-center text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
              {['S', 'T', 'Q', 'Q', 'S', 'S', 'D'].map((dayLabel, index) => (
                <span key={`${dayLabel}-${index}`}>{dayLabel}</span>
              ))}
            </div>

            <div className="mt-3 grid grid-cols-7 gap-2">
              {agendaMonthDays.map((dayDate) => {
                const dayMoment = moment.tz(dayDate, timeZone);
                const eventCount = getAgendaDayEventCount(dayDate);
                const isSelected = dayMoment.isSame(agendaSelectedDay, 'day');
                const isCurrentMonth = dayMoment.isSame(agendaSelectedDay, 'month');
                const isToday = dayMoment.isSame(moment.tz(new Date(), timeZone), 'day');
                const occupancyLevel = Math.min(eventCount, 4);

                return (
                  <button
                    key={dayMoment.format('YYYY-MM-DD')}
                    type="button"
                    onClick={() => setCurrentDate(dayMoment.toDate())}
                    className={`relative flex h-11 items-center justify-center rounded-full text-sm font-medium transition ${
                      isSelected
                        ? 'bg-slate-900 text-white shadow-sm'
                        : occupancyLevel > 0
                        ? 'text-slate-700 hover:bg-slate-100'
                        : 'text-slate-500 hover:bg-slate-50'
                    } ${
                      !isCurrentMonth ? 'opacity-35' : ''
                    } ${isToday && !isSelected ? 'ring-2 ring-sky-200' : ''}`}
                    style={
                      !isSelected && occupancyLevel > 0
                        ? { backgroundColor: `rgba(148, 163, 184, ${0.12 + (occupancyLevel * 0.12)})` }
                        : undefined
                    }
                    title={`${dayMoment.format('DD/MM/YYYY')} • ${eventCount} agendamento(s)`}
                  >
                    <span>{dayMoment.format('D')}</span>
                    {eventCount > 0 && (
                      <span className={`absolute bottom-1 right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-semibold ${
                        isSelected ? 'bg-white/20 text-white' : 'bg-white text-slate-500 shadow-sm'
                      }`}>
                        {eventCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

          </aside>
        </div>
      </>
    );
  };
  const selectedEventStatus = resolveAgendaEventUiStatus(selectedEvent);
  const selectedEventHasEvolution = hasEvolution(selectedEvent);
  const selectedEventMedicalRecordPath = getMedicalRecordPath();
  const selectedEventCanEmitAttendanceCertificate = canEmitAttendanceCertificate(selectedEvent);
  const canOpenSelectedEventMedicalRecord = Boolean(
    canUseEvolutionShortcut &&
    selectedEvent?.patientId &&
    selectedEventMedicalRecordPath
  );
  const canEvolveSelectedEvent = Boolean(
    canOpenSelectedEventMedicalRecord &&
    !selectedEventHasEvolution &&
    selectedEventStatus !== 'cancelled' &&
    selectedEventStatus !== 'no_show'
  );
  const selectedEventEvolutionMessage = !selectedEvent?.patientId
    ? 'Vincule um paciente ao agendamento para abrir a evolução já contextualizada.'
    : !selectedEventMedicalRecordPath
      ? 'O perfil atual não possui acesso configurado ao prontuário.'
      : selectedEventStatus === 'cancelled' || selectedEventStatus === 'no_show'
        ? 'Agendamentos cancelados ou com falta não podem gerar evolução.'
        : selectedEventHasEvolution
          ? 'Este agendamento já possui evolução registrada.'
          : 'Abra o prontuário com paciente e agendamento pré-selecionados para registrar a evolução com menos cliques.';
  const selectedEventPrimaryStatus = getAgendaPrimaryStatusMeta(selectedEvent);
  const SelectedEventPrimaryStatusIcon = selectedEventPrimaryStatus.icon;
  const canDeleteSelectedEvent = canDeleteAgendaEntries && !isProtectedAppointment(selectedEvent);
  const canEditSelectedEvent = canEditAgendaEntries && selectedEventStatus === 'scheduled';
  const canSetCompletedStatus = canManageAppointmentStatus && (selectedEventStatus === 'scheduled' || selectedEventStatus === 'no_billed');
  const canReopenSelectedEvent = canManageAppointmentStatus && selectedEventStatus === 'no_evaluated';
  const renderCalendarToolbar = (toolbarProps: ToolbarProps<AgendaEvent>) => (
    <AgendaToolbar
      date={toolbarProps.date}
      view={toolbarProps.view}
      views={toolbarProps.views}
      onNavigate={(action) => toolbarProps.onNavigate(action)}
      onView={toolbarProps.onView}
      timeZone={timeZone}
    />
  );
  const calendarMinTime = parseDateAndTimeInTimeZone('2024-01-01', `${String(AGENDA_START_HOUR).padStart(2, '0')}:00`, timeZone);
  const calendarMaxTime = parseDateAndTimeInTimeZone('2024-01-01', `${String(AGENDA_END_HOUR).padStart(2, '0')}:00`, timeZone);
  const statusLegendItems = [
    { key: 'completed', label: 'Evolução feita', icon: CheckCircle },
    { key: 'no_evaluated', label: 'Sem evolução', icon: AlertTriangle },
    { key: 'scheduled', label: 'Agendado', icon: Clock },
    { key: 'no_show', label: 'Falta / cancelado', icon: XCircle },
    { key: 'no_billed', label: 'A faturar', icon: DollarSign },
  ] as const;

  const calendarLegendItems = calendarFilterOptions;
  const isAgendaDayView = !showListView && currentView === Views.AGENDA;

  const financialOriginGroups: Record<FinancialOriginKind, FinancialOriginOption[]> = {
    pacote: financialOriginPreview?.options.filter((option) => option.kind === 'pacote') || [],
    plano: financialOriginPreview?.options.filter((option) => option.kind === 'plano') || [],
    avulsa: financialOriginPreview?.options.filter((option) => option.kind === 'avulsa') || [],
  };
  const selectedFinancialOption = financialOriginPreview?.options.find(
    (option) => option.id === selectedFinancialOriginId,
  ) || null;

  if (isLoading) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#eef3f1]">
        <div className="absolute -left-16 top-16 h-72 w-72 rounded-full bg-emerald-200/40 blur-3xl" />
        <div className="absolute right-0 top-0 h-80 w-80 rounded-full bg-orange-200/35 blur-3xl" />
        <div className="relative rounded-[28px] border border-white/70 bg-white/70 px-10 py-12 text-center shadow-[0_28px_70px_rgba(15,23,42,0.12)] backdrop-blur-2xl">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-emerald-600" />
          <p className="text-lg font-medium text-slate-700">Carregando agenda...</p>
          <p className="mt-2 text-sm text-slate-500">Conectando ao Supabase...</p>
        </div>
      </div>
    );
  }

  if (!isClinicTimeZoneReady) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-[1800px] px-4 py-6 lg:px-8 lg:py-8">
          <div className="rounded-[28px] border border-amber-200 bg-white px-6 py-8 shadow-sm">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div>
                <h1 className="text-xl font-semibold text-slate-900">Agenda temporariamente indisponível</h1>
                <p className="mt-2 text-sm text-amber-900">
                  {isClinicProfileLoading
                    ? 'Carregando e validando o fuso horário da clínica antes de exibir os agendamentos.'
                    : 'Não foi possível confirmar o fuso horário da clínica. Recarregue a página; nenhum horário pode ser criado ou alterado enquanto isso.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-[1800px] px-4 py-6 lg:px-8 lg:py-8">
        <div className="rounded-[28px] border border-slate-200 bg-white px-5 py-5 shadow-sm lg:px-6 lg:py-6">
          <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-xl border border-slate-200 bg-white p-2.5 shadow-sm">
                <CalendarIcon className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Agenda</h1>
                {lastSyncTime && (
                  <p className="mt-1 text-sm text-slate-500">
                    Atualizada às {formatInTimeZone(lastSyncTime, 'HH:mm:ss', timeZone)}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {calendarLegendItems.length > 0 && (
                <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 shadow-sm">
                  <span className="whitespace-nowrap">Profissional</span>
                  <select
                    value={activeCalendarFilterKey}
                    onChange={(e) => setActiveCalendarFilterKey(e.target.value)}
                    className="min-w-[220px] bg-transparent font-medium text-slate-800 outline-none"
                  >
                    <option value="all">Todos os profissionais</option>
                    {calendarLegendItems.map((calendarItem) => (
                      <option key={calendarItem.id} value={calendarItem.id}>
                        {calendarItem.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {!showListView && (
                <button
                  type="button"
                  onClick={() => setShowLegendPanel((current) => !current)}
                  className={`inline-flex items-center rounded-xl border px-4 py-2 text-sm font-medium shadow-sm transition ${
                    showLegendPanel
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  Legenda
                </button>
              )}

              <button
                onClick={() => {
                  console.log('🔵 Alternando visualização:', !showListView ? 'LISTA' : 'CALENDÁRIO');
                  setShowListView(!showListView);
                  setSelectionMode(false);
                  setSelectedEvents(new Set());
                  setShowLegendPanel(false);
                  if (!showListView) {
                    setDateRangeConfirmed(false);
                    setPendingStartDate('');
                    setPendingEndDate('');
                    setStartDateFilter('');
                    setEndDateFilter('');
                  }
                }}
                className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                {showListView ? (
                  <>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    Calendário
                  </>
                ) : (
                  <>
                    <List className="mr-2 h-4 w-4" />
                    Lista
                  </>
                )}
              </button>

              {!showListView && canManageAgenda && (
                <button
                  onClick={() => {
                    setSelectionMode(!selectionMode);
                    if (selectionMode) {
                      setSelectedEvents(new Set());
                    }
                  }}
                  className={`inline-flex items-center rounded-xl border px-4 py-2 text-sm font-medium shadow-sm transition ${
                    selectionMode
                      ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {selectionMode ? (
                    <>
                      <CheckSquare className="mr-2 h-4 w-4" />
                      Modo seleção
                    </>
                  ) : (
                    <>
                      <MousePointerClick className="mr-2 h-4 w-4" />
                      Seleção múltipla
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {!showListView && showLegendPanel && (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 shadow-sm">
              <div className="grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)]">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Status</p>
                  <div className="mt-3 space-y-2.5">
                    {statusLegendItems.map(({ key, label, icon: Icon }) => (
                      <div key={key} className="flex items-center gap-3 text-sm text-slate-600">
                        <Icon className="h-4 w-4" style={{ color: AGENDA_STATUS_COLORS[key] }} />
                        <span>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Profissionais</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {calendarLegendItems.length > 0 ? (
                      calendarLegendItems.map((calendarItem) => (
                        <span
                          key={calendarItem.id}
                          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700"
                        >
                          <span
                            className="h-2.5 w-2.5 rounded-full"
                            style={{ backgroundColor: calendarItem.accentColor }}
                          />
                          {calendarItem.label}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-slate-500">Nenhum profissional identificado.</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {canManageAgenda && selectedEvents.size > 0 && (
            <div className="mt-6 rounded-[24px] border border-blue-200 bg-blue-50/90 px-4 py-4 shadow-sm">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <span className="font-medium text-blue-900">
                    {selectedEvents.size} evento(s) selecionado(s)
                  </span>
                  <button
                    onClick={() => setSelectedEvents(new Set())}
                    className="font-medium text-blue-700 underline underline-offset-2 hover:text-blue-900"
                  >
                    Cancelar seleção
                  </button>
                  {!showListView && selectionMode && (
                    <button
                      onClick={selectAllVisibleEvents}
                      className="font-medium text-blue-700 underline underline-offset-2 hover:text-blue-900"
                    >
                      Selecionar todos visíveis
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setShowBulkDeleteConfirm(true)}
                  className="inline-flex items-center rounded-2xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-red-700"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Excluir selecionados
                </button>
              </div>
            </div>
          )}

        </div>

        <div className="mt-5">
          {showListView ? (
            <div
              className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm"
              key="list-view"
            >
              {!dateRangeConfirmed ? (
                <div className="p-8">
                  <div className="mx-auto max-w-2xl">
                    <div className="mb-8 text-center">
                      <CalendarIcon className="mx-auto mb-4 h-16 w-16 text-emerald-600" />
                      <h3 className="mb-2 text-2xl font-bold text-slate-900">Selecione o período para visualizar</h3>
                      <p className="text-slate-600">
                        Escolha um intervalo de datas para carregar os eventos da agenda.
                        Isso ajuda a prevenir problemas de performance com grandes volumes de dados.
                      </p>
                    </div>

                    <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
                      <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                          <label className="mb-2 block text-sm font-semibold text-slate-900">
                            Data Inicial *
                          </label>
                          <input
                            type="date"
                            value={pendingStartDate}
                            onChange={(e) => setPendingStartDate(e.target.value)}
                            className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-lg focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                            placeholder="Selecione a data inicial"
                          />
                        </div>
                        <div>
                          <label className="mb-2 block text-sm font-semibold text-slate-900">
                            Data Final *
                          </label>
                          <input
                            type="date"
                            value={pendingEndDate}
                            onChange={(e) => setPendingEndDate(e.target.value)}
                            className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-lg focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                            placeholder="Selecione a data final"
                          />
                        </div>
                      </div>

                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <button
                          onClick={() => {
                            const currentMonth = moment.tz(new Date(), timeZone);
                            setPendingStartDate(currentMonth.clone().startOf('month').format('YYYY-MM-DD'));
                            setPendingEndDate(currentMonth.clone().endOf('month').format('YYYY-MM-DD'));
                          }}
                          className="text-sm font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-900"
                        >
                          Usar mês atual
                        </button>

                        <button
                          onClick={() => {
                            if (!pendingStartDate || !pendingEndDate) {
                              toast.warning('Por favor, selecione as datas inicial e final');
                              return;
                            }
                            if (pendingEndDate < pendingStartDate) {
                              toast.error('A data final deve ser posterior à data inicial');
                              return;
                            }

                            const [startYear, startMonth] = pendingStartDate.split('-').map(Number);
                            const [endYear, endMonth] = pendingEndDate.split('-').map(Number);
                            const diffMonths = (endYear - startYear) * 12 + (endMonth - startMonth);

                            if (diffMonths > 6) {
                              toast.warning('⚠️ Período muito grande! Isso pode afetar a performance.');
                            }

                            setStartDateFilter(pendingStartDate);
                            setEndDateFilter(pendingEndDate);
                            setDateRangeConfirmed(true);
                            toast.success('Período selecionado! Carregando eventos...');
                          }}
                          disabled={!pendingStartDate || !pendingEndDate}
                          className="inline-flex items-center justify-center rounded-2xl bg-emerald-600 px-6 py-3 font-medium text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <CalendarIcon className="mr-2 h-5 w-5" />
                          Carregar Eventos
                        </button>
                      </div>
                    </div>

                    <div className="mt-6 text-center text-sm text-slate-500">
                      <p>Dica: selecione períodos menores para melhor performance.</p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="border-b border-slate-200/80 p-4">
                    <div className="mb-4 rounded-[22px] border border-green-200 bg-green-50/90 p-4">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex items-center">
                          <CheckCircle className="mr-2 h-5 w-5 text-green-600" />
                          <div>
                            <p className="text-sm font-medium text-green-900">
                              Período ativo: {startDateFilter.split('-').reverse().join('/')} até {endDateFilter.split('-').reverse().join('/')}
                            </p>
                            <p className="text-xs text-green-700">
                              {filteredEvents.length} evento(s) encontrado(s)
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setDateRangeConfirmed(false);
                            setPendingStartDate('');
                            setPendingEndDate('');
                            setSelectedEvents(new Set());
                            toast.info('Selecione um novo período');
                          }}
                          className="rounded-xl border border-green-300 bg-white px-4 py-2 text-sm font-medium text-green-700 transition hover:bg-green-50"
                        >
                          Alterar período
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-slate-900">Lista de Eventos</h3>
                      {canManageAgenda && (
                        <button
                        onClick={selectAllEvents}
                        className="text-sm font-medium text-emerald-700 hover:text-emerald-900"
                      >
                        {selectedEvents.size === filteredEvents.length && filteredEvents.length > 0 ? 'Desselecionar todos' : 'Selecionar todos'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200/80">
                      <thead className="bg-slate-50/80">
                        <tr>
                          <th className="w-12 px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                            <input
                              type="checkbox"
                              checked={selectedEvents.size === filteredEvents.length && filteredEvents.length > 0}
                              onChange={selectAllEvents}
                              disabled={!canManageAgenda}
                              className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                            />
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Paciente/Título</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Data/Hora</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Profissional</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Situação</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200/80 bg-white/80">
                        {filteredEvents.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-6 py-8 text-center">
                              <div className="py-8 text-center">
                                <CalendarIcon className="mx-auto mb-3 h-12 w-12 text-slate-400" />
                                <p className="font-medium text-slate-600">Nenhum evento encontrado no período selecionado</p>
                                <p className="mt-1 text-sm text-slate-500">
                                  Tente selecionar um período diferente.
                                </p>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          filteredEvents.map((event) => {
                            const resolvedStatus = resolveAgendaEventUiStatus(event);
                            const statusMeta = getAgendaPrimaryStatusMeta(event);
                            const StatusIcon = statusMeta.icon;
                            const statusBadgeClassName =
                              resolvedStatus === 'completed'
                                ? 'bg-emerald-100 text-emerald-800'
                                : resolvedStatus === 'no_billed'
                                  ? 'bg-violet-100 text-violet-800'
                                  : resolvedStatus === 'no_evaluated'
                                    ? 'bg-amber-100 text-amber-800'
                                    : resolvedStatus === 'no_show'
                                      ? 'bg-red-100 text-red-800'
                                      : resolvedStatus === 'cancelled'
                                        ? 'bg-slate-100 text-slate-700'
                                        : 'bg-blue-100 text-blue-800';

                            return (
                            <tr key={event.id} className={`transition hover:bg-slate-50/90 ${selectedEvents.has(event.id) ? 'bg-blue-50/80' : ''}`}>
                              <td className="whitespace-nowrap px-6 py-4">
                                <input
                                  type="checkbox"
                                  checked={selectedEvents.has(event.id)}
                                  onChange={() => toggleEventSelection(event.id)}
                                  disabled={!canManageAgenda}
                                  className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                                />
                              </td>
                              <td className="px-6 py-4 text-sm text-slate-900">
                                <div className="min-w-0">
                                  <p className="truncate font-medium text-slate-900">{event.title}</p>
                                  <p className="mt-1 truncate text-xs text-slate-500">
                                    {event.location
                                      ? formatAppointmentLocation(event.location)
                                      : event.locationIssue || 'Sem local informado'}
                                  </p>
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-900">
                                {formatInTimeZone(event.start, 'DD/MM/YYYY HH:mm', timeZone)}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-900">
                                {event.calendar_name || '-'}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-900">
                                {event.location
                                  ? formatAppointmentLocation(event.location)
                                  : event.locationIssue || '-'}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4">
                                {hasEvolution(event) ? (
                                  <span className="flex w-fit items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">
                                    <CheckCircle className="h-3 w-3" />
                                    Realizada
                                  </span>
                                ) : (
                                  <span className="flex w-fit items-center gap-1 rounded-full bg-orange-100 px-2 py-1 text-xs font-semibold text-orange-800">
                                    <AlertTriangle className="h-3 w-3" />
                                    Pendente
                                  </span>
                                )}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4">
                                <div className="flex flex-col gap-1">
                                  {event.status === 'completed' && (
                                    <span className="flex w-fit items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">
                                      <CheckCircle className="h-3 w-3" />
                                      Concluído
                                    </span>
                                  )}
                                  {event.status === 'cancelled' && (
                                    <span className="flex w-fit items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-800">
                                      <XCircle className="h-3 w-3" />
                                      Cancelado
                                    </span>
                                  )}
                                  {event.status === 'no_show' && (
                                    <span className="flex w-fit items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-800">
                                      <AlertTriangle className="h-3 w-3" />
                                      Falta
                                    </span>
                                  )}
                                  {event.status === 'scheduled' && (
                                    <span className="flex w-fit items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-800">
                                      <Clock className="h-3 w-3" />
                                      Agendado
                                    </span>
                                  )}
                                  <div className="flex items-center gap-1">
                                    {event.isBilled && (
                                      <span className="flex w-fit items-center gap-1 rounded-full bg-purple-100 px-2 py-1 text-xs font-semibold text-purple-800" title="Faturado">
                                        <DollarSign className="h-3 w-3" />
                                        Faturado
                                      </span>
                                    )}
                                    {event.patientId && event.financialAppointmentId && (
                                      <span className="flex w-fit items-center gap-1 rounded-full bg-indigo-100 px-2 py-1 text-xs font-semibold text-indigo-800" title="Vinculado ao sistema financeiro">
                                        <Package className="h-3 w-3" />
                                        Integrado
                                      </span>
                                    )}
                                    {event.financialInvoiceId && (
                                      <span className="flex w-fit items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-800" title="Fatura do novo financeiro gerada">
                                        <DollarSign className="h-3 w-3" />
                                        Fatura gerada
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                                <div className="flex items-center gap-3">
                                  <button
                                    onClick={() => handleSelectEvent(event)}
                                    className="font-medium text-blue-600 transition hover:text-blue-900 hover:underline"
                                    title="Visualizar"
                                  >
                                    Abrir
                                  </button>
                                  {canManageAgenda && (
                                    <button
                                      onClick={() => setShowDeleteConfirm(event.id)}
                                      className="font-medium text-red-600 transition hover:text-red-900 hover:underline"
                                      title="Excluir"
                                    >
                                      Excluir
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          ) : isAgendaDayView ? (
            <div
              className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm lg:p-5"
              key="agenda-day-view"
            >
              {renderAgendaDayView()}
            </div>
          ) : (
            <div
              className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm lg:p-5"
              key="calendar-view"
            >
              <div style={{ height: '80vh' }}>
                <Calendar
                  localizer={localizer}
                  events={filteredCalendarEvents}
                  startAccessor="start"
                  endAccessor="end"
                  titleAccessor="title"
                  tooltipAccessor={(event) => getAgendaEventTooltip(event, timeZone)}
                  view={currentView}
                  onView={setCurrentView}
                  date={currentDate}
                  onNavigate={setCurrentDate}
                  onSelectSlot={handleSelectSlot}
                  onSelectEvent={handleSelectEvent}
                  selectable={canManageAgenda && isClinicTimeZoneReady}
                  popup={true}
                  messages={messages}
                  formats={formats}
                  eventPropGetter={eventStyleGetter}
                  culture="pt-BR"
                  views={AGENDA_VIEW_ORDER}
                  defaultView={Views.AGENDA}
                  step={30}
                  timeslots={2}
                  min={calendarMinTime}
                  max={calendarMaxTime}
                  scrollToTime={calendarMinTime}
                  dayLayoutAlgorithm="no-overlap"
                  components={{
                    toolbar: renderCalendarToolbar,
                    event: AgendaEventCard,
                    agenda: {
                      event: AgendaListEventCard,
                    },
                  }}
                  className="agenda-calendar"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Novo agendamento</h3>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={closeCreateModal}
                disabled={isCreatingAppointments}
                aria-label="Fechar criação de agendamento"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="px-6 py-4 space-y-4">
              {(createFormClientId !== activeClientId || createFormTimeZone !== timeZone) && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  A clínica ou o fuso horário mudou enquanto este formulário estava aberto. Feche, reabra e revise os horários.
                </div>
              )}
              <p className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
                Horário local da clínica: {getTimeZoneLabel(createFormTimeZone)} ({createFormTimeZone})
              </p>
              <div>
                <span className="block text-sm font-medium text-gray-700 mb-2">Formato</span>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {([
                    { id: 'single', label: 'Único', description: 'Um paciente e um horário' },
                    { id: 'series', label: 'Série', description: 'Um paciente e várias datas' },
                    { id: 'group', label: 'Grupo', description: 'Vários pacientes no mesmo horário' },
                  ] as Array<{ id: AppointmentCreateMode; label: string; description: string }>).map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => {
                        setCreateMode(option.id);
                        setForm((currentForm) => ({ ...currentForm, patientId: '', subject: '' }));
                        setGroupPatientIds([]);
                        setGroupPatientPickerId('');
                      }}
                      className={`rounded-lg border px-3 py-2 text-left transition ${
                        createMode === option.id
                          ? 'border-primary-500 bg-primary-50 text-primary-800'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <span className="block text-sm font-semibold">{option.label}</span>
                      <span className="block text-xs opacity-80">{option.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {createMode === 'group' ? 'Pacientes do grupo *' : 'Paciente *'}
                </label>
                {loadingPatients ? (
                  <div className="mt-1 w-full rounded-md border-gray-300 border p-2 text-sm text-gray-500">
                    Carregando pacientes...
                  </div>
                ) : createMode === 'group' ? (
                  <div className="space-y-3">
                    <PatientSearchSelect
                      patients={patients.filter((patient) => !groupPatientIds.includes(patient.id))}
                      selectedPatientId={groupPatientPickerId}
                      onSelect={(patientId) => {
                        if (!patientId || groupPatientIds.includes(patientId)) return;
                        if (groupPatientIds.length >= APPOINTMENT_BATCH_LIMITS.groupPatients) {
                          toast.error(`O grupo pode ter no máximo ${APPOINTMENT_BATCH_LIMITS.groupPatients} pacientes.`);
                          return;
                        }
                        setGroupPatientIds((currentIds) => [...currentIds, patientId]);
                        setGroupPatientPickerId('');
                      }}
                      placeholder="Buscar e adicionar paciente..."
                      showActiveFilter={false}
                      allowInactiveSelection={false}
                      allowManualOther={false}
                    />

                    {groupPatientIds.length > 0 ? (
                      <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          {groupPatientIds.length} paciente{groupPatientIds.length === 1 ? '' : 's'} selecionado{groupPatientIds.length === 1 ? '' : 's'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {groupPatientIds.map((patientId) => {
                            const patient = patients.find((item) => item.id === patientId);
                            return (
                              <span
                                key={patientId}
                                className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-1 text-sm text-gray-700"
                              >
                                {getPatientDisplayName(patient)}
                                <button
                                  type="button"
                                  onClick={() => setGroupPatientIds((currentIds) =>
                                    currentIds.filter((currentId) => currentId !== patientId)
                                  )}
                                  className="text-gray-400 hover:text-red-600"
                                  aria-label={`Remover ${getPatientDisplayName(patient)} do grupo`}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">Adicione pelo menos dois pacientes.</p>
                    )}
                  </div>
                ) : (
                  <PatientSearchSelect
                    patients={patients}
                    selectedPatientId={form.patientId}
                    onSelect={(patientId) => {
                      const patient = patients.find((item) => item.id === patientId);
                      setForm((currentForm) => ({
                        ...currentForm,
                        patientId,
                        subject: getPatientDisplayName(patient),
                      }));
                    }}
                    placeholder="Buscar paciente por nome ou prontuário..."
                    showActiveFilter={true}
                    allowInactiveSelection={false}
                    allowManualOther={false}
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Serviço agendado *</label>
                <select
                  value={form.serviceId}
                  onChange={(e) => setForm((f) => ({ ...f, serviceId: e.target.value }))}
                  className="w-full rounded-md border-gray-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  required
                  disabled={loadingAgendaServices}
                >
                  <option value="">{loadingAgendaServices ? 'Carregando serviços...' : 'Selecione o serviço'}</option>
                  {agendaServices.map((service) => (
                    <option key={service.serviceId} value={service.serviceId}>
                      {service.serviceName}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">O serviço será usado para filtrar pacotes e regras de preço na conclusão.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Profissional
                  </label>

                  {selectableProfessionals.length > 0 ? (
                    <>
                      <select
                        className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white"
                        value={form.calendar_name}
                        onChange={(e) => setForm((f) => ({ ...f, calendar_name: e.target.value }))}
                      >
                        <option value="">Selecione um calendário existente</option>
                        {selectableProfessionals.map((professional) => (
                          <option key={professional.userId} value={professional.userId}>
                            {professional.label}
                          </option>
                        ))}
                      </select>
                    </>
                  ) : (
                    <div className="mt-1 rounded-md border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                      Nenhum calendário existente disponível para seleção.
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Local *</label>
                  <select
                    className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white"
                    value={form.location}
                    onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                    required
                  >
                    <option value="">Selecione um local</option>
                    <option value="Clinica">Clínica</option>
                    <option value="Domicilio">Domicílio</option>
                    <option value="Hospitalar">Hospitalar</option>
                  </select>
                </div>
              </div>

              {createMode !== 'series' && (
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Início</label>
                      <input
                        type="datetime-local"
                        className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                        value={form.start}
                        onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Fim</label>
                      <input
                        type="datetime-local"
                        className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                        value={form.end}
                        onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))}
                        required
                      />
                    </div>
                  </div>
                </div>
              )}

              {createMode !== 'single' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
                  Todos os itens serão criados como agendados em uma única operação. Esta ação não reserva nem consome sessões de pacote e não calcula repasse.
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  disabled={isCreatingAppointments}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={
                    isCreatingAppointments
                    || createFormClientId !== activeClientId
                    || createFormTimeZone !== timeZone
                    || (createMode === 'series' && Boolean(seriesPreviewError))
                  }
                  className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {isCreatingAppointments
                    ? 'Criando...'
                    : createMode === 'series'
                      ? `Criar ${seriesPreviewRanges.length} agendamentos`
                      : createMode === 'group'
                        ? `Criar ${groupPatientIds.length} agendamentos`
                        : 'Criar agendamento'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Event Details Modal */}
      {showEventModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Detalhes do Evento</h3>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setShowEventModal(false)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-slate-500">
                      <Eye className="h-4 w-4" />
                      <span className="text-xs font-semibold uppercase tracking-[0.18em]">Evento selecionado</span>
                    </div>
                    <h4 className="mt-1 truncate text-xl font-semibold text-slate-900">
                      {selectedEvent.title}
                    </h4>
                    <p className="mt-1 text-sm text-slate-600">
                      {selectedEvent.calendar_name || 'Não informado'} • {formatInTimeZone(selectedEvent.start, 'DD/MM/YYYY [às] HH:mm', timeZone)}
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                      {selectedEvent.location
                        ? formatAppointmentLocation(selectedEvent.location)
                        : selectedEvent.locationIssue || 'Local não informado'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Profissional</label>
                    <p className="text-sm text-gray-900">{selectedEvent.calendar_name || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Serviço</label>
                    <p className="text-sm text-gray-900">{selectedEvent.serviceName || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Local</label>
                    <p className="text-sm text-gray-900">
                      {selectedEvent.location
                        ? formatAppointmentLocation(selectedEvent.location)
                        : selectedEvent.locationIssue || 'Não informado'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">ID do Evento</label>
                    <p className="text-xs text-gray-600 font-mono">{selectedEvent.id}</p>
                  </div>
                </div>
              </div>

              <div className={`grid gap-4 ${canEditAgendaEntries ? 'md:grid-cols-2' : ''}`}>
                {canEditAgendaEntries && (
                  <div className={`rounded-2xl border p-4 shadow-sm ${canEditSelectedEvent ? 'border-blue-200 bg-blue-50/80' : 'border-slate-200 bg-slate-50'}`}>
                    <h4 className={`text-base font-semibold ${canEditSelectedEvent ? 'text-blue-900' : 'text-slate-800'}`}>
                      Editar Agendamento
                    </h4>
                    <p className={`mt-2 text-sm ${canEditSelectedEvent ? 'text-blue-700' : 'text-slate-600'}`}>
                      {getEditSectionMessage(selectedEventStatus)}
                    </p>
                    <button
                      onClick={() => handleEditClick(selectedEvent)}
                      disabled={!canEditSelectedEvent}
                      className={`mt-4 inline-flex items-center rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                        canEditSelectedEvent
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-slate-200 text-slate-500 cursor-not-allowed'
                      }`}
                    >
                      <Edit className="mr-2 h-4 w-4" />
                      Editar evento
                    </button>
                  </div>
                )}

                <div className={`rounded-2xl border p-4 shadow-sm ${canEvolveSelectedEvent ? 'border-emerald-200 bg-emerald-50/80' : 'border-slate-200 bg-slate-50'}`}>
                  <h4 className={`text-base font-semibold ${canEvolveSelectedEvent ? 'text-emerald-900' : 'text-slate-800'}`}>
                    Evolução Clínica
                  </h4>
                  <p className={`mt-2 text-sm ${canEvolveSelectedEvent ? 'text-emerald-700' : 'text-slate-600'}`}>
                    {selectedEventEvolutionMessage}
                  </p>
                  <button
                    onClick={() => handleOpenMedicalRecordFromAppointment(selectedEvent)}
                    disabled={!canEvolveSelectedEvent}
                    className={`mt-4 inline-flex items-center rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                      canEvolveSelectedEvent
                        ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                        : 'bg-slate-200 text-slate-500 cursor-not-allowed'
                    }`}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Evoluir este agendamento
                  </button>
                </div>
              </div>

              <details className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold text-slate-800">
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-slate-500" />
                      Auditoria do agendamento
                    </span>
                    <span className="text-xs font-medium text-slate-400">Ver histórico</span>
                  </div>
                </summary>
                <div className="border-t border-slate-200 px-4 py-4">
                  {loadingAppointmentAuditTrail ? (
                    <p className="text-sm text-slate-600">Carregando histórico...</p>
                  ) : appointmentAuditTrail.length === 0 ? (
                    <p className="text-sm text-slate-600">Nenhum evento de auditoria registrado para este agendamento.</p>
                  ) : (
                    <div className="space-y-3">
                      {appointmentAuditTrail.map((entry) => (
                        <div key={entry.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                            <span className="font-semibold text-slate-900">{AGENDA_STATUS_LABELS[parseAgendaEventStatus(entry.newStatus || entry.action)] || entry.action}</span>
                            <span className="text-slate-500">
                              {moment(entry.createdAt).format('DD/MM/YYYY [às] HH:mm')}
                            </span>
                            <span className="text-slate-600">
                              {entry.actorDisplayName || entry.actorEmail || entry.actorUserId || 'Usuário não identificado'}
                            </span>
                          </div>
                          {(entry.previousStatus || entry.newStatus) && (
                            <p className="mt-1 text-xs text-slate-500">
                              {entry.previousStatus || 'sem status'} → {entry.newStatus || 'sem status'}
                            </p>
                          )}
                          <p className="mt-2 text-sm text-slate-700">
                            {entry.notes?.trim() || 'Sem motivo/observação informado.'}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </details>

              <details className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold text-slate-800">
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-slate-500" />
                      Atestado de presença
                    </span>
                    <span className="text-xs font-medium text-slate-400">Emissão</span>
                  </div>
                </summary>
                <div className="border-t border-slate-200 px-4 py-4">
                  {selectedEventCanEmitAttendanceCertificate ? (
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-sm text-slate-600">
                          Gere um atestado com os horários da sessão e revise todos os dados antes de baixar ou imprimir.
                        </p>
                      </div>
                      <button
                        onClick={() => void handleOpenAttendanceCertificate(selectedEvent)}
                        disabled={isGeneratingAttendanceCertificate}
                        className="inline-flex items-center rounded-xl bg-sky-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        {isGeneratingAttendanceCertificate ? 'Preparando...' : 'Emitir atestado'}
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-600">
                      O atestado só pode ser emitido para atendimentos realizados.
                    </p>
                  )}
                </div>
              </details>

              <details className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold text-slate-800">
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2">
                      <CheckSquare className="h-4 w-4 text-slate-500" />
                      Gerenciar status
                    </span>
                    <span className="text-xs font-medium text-slate-400">Ações disponíveis</span>
                  </div>
                </summary>
                <div className="border-t border-slate-200 px-4 py-4">
                  {canManageAppointmentStatus ? (
                    <>
                      <p className="text-sm text-slate-600">
                        {getStatusManagementMessage(selectedEventStatus)}
                      </p>
                      {selectedEventStatus === 'scheduled' && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          <button
                            onClick={() => handleUpdateStatus(selectedEvent.id, 'completed')}
                            className="inline-flex items-center rounded-xl bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
                            disabled={isUpdating || !canSetCompletedStatus}
                          >
                            <CheckCircle className="mr-2 h-4 w-4" />
                            {isUpdating ? 'Processando...' : 'Concluir'}
                          </button>
                          <button
                            onClick={() => openStatusResolutionModal(selectedEvent, 'no_show')}
                            className="inline-flex items-center rounded-xl bg-orange-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-600"
                            disabled={isUpdating}
                          >
                            <AlertTriangle className="mr-2 h-4 w-4" />
                            {isUpdating ? 'Processando...' : 'Registrar ausência'}
                          </button>
                          <button
                            onClick={() => openStatusResolutionModal(selectedEvent, 'cancelled')}
                            className="inline-flex items-center rounded-xl bg-slate-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-800"
                            disabled={isUpdating}
                          >
                            <XCircle className="mr-2 h-4 w-4" />
                            {isUpdating ? 'Processando...' : 'Cancelar'}
                          </button>
                        </div>
                      )}

                      {selectedEventStatus === 'no_billed' && (
                        <div className="mt-4 flex flex-wrap items-center gap-3">
                          <button
                            onClick={() => handleUpdateStatus(selectedEvent.id, 'completed')}
                            className="inline-flex items-center rounded-xl bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
                            disabled={isUpdating || !canSetCompletedStatus}
                          >
                            <CheckCircle className="mr-2 h-4 w-4" />
                            {isUpdating ? 'Processando...' : 'Concluir'}
                          </button>
                          <p className="text-xs text-slate-500">
                            Evolução já vinculada; concluir ajusta o status final e o faturamento, quando aplicável.
                          </p>
                        </div>
                      )}

                      {selectedEventStatus === 'no_evaluated' && (
                        <div className="mt-4 flex flex-wrap items-center gap-3">
                          <button
                            onClick={() => handleReopenNoEvaluated(selectedEvent)}
                            className="inline-flex items-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                            disabled={isUpdating || !canReopenSelectedEvent}
                          >
                            <RotateCcw className="mr-2 h-4 w-4" />
                            {isUpdating ? 'Processando...' : 'Voltar para agendado'}
                          </button>
                          <p className="text-xs text-slate-500">
                            Use apenas quando a evolução foi removida ou precisa ser refeita.
                          </p>
                        </div>
                      )}

                      {(selectedEventStatus === 'completed' || selectedEventStatus === 'cancelled' || selectedEventStatus === 'no_show') && (
                        <p className="mt-4 text-sm text-slate-600">
                          Nenhuma ação de status está disponível para este estado.
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-slate-600">
                      Este perfil possui acesso somente para visualização deste agendamento.
                    </p>
                  )}
                </div>
              </details>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-4">
              <div className="flex flex-col items-start">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(selectedEvent.id);
                    setShowEventModal(false);
                  }}
                  disabled={!canDeleteSelectedEvent}
                  className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Excluir Evento
                </button>
              </div>
              <button
                onClick={() => setShowEventModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {showFinancialOriginModal && financialOriginPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Como este atendimento será coberto?</h3>
                <p className="text-xs text-slate-500 mt-1">Escolha a origem financeira antes de concluir o atendimento.</p>
              </div>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={handleCancelFinancialOriginSelection}
                disabled={isUpdating}
                aria-label="Fechar seleção de origem financeira"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                O sistema deixou pré-selecionada a alternativa mais adequada. Você pode alterá-la quando necessário.
              </div>

              {financialOriginPreview.options.length === 0 ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  Não há pacote com saldo nem regra de preço elegível para este atendimento. Cadastre uma origem financeira antes de concluí-lo.
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    {([
                      ['pacote', 'Pacote'],
                      ['plano', 'Plano/convênio'],
                      ['avulsa', 'Particular/avulso'],
                    ] as const).map(([kind, label]) => {
                      const options = financialOriginGroups[kind];
                      const isActive = selectedFinancialOriginKind === kind;
                      return (
                        <button
                          key={kind}
                          type="button"
                          onClick={() => {
                            const firstOption = options[0];
                            setSelectedFinancialOriginKind(kind);
                            if (firstOption) setSelectedFinancialOriginId(firstOption.id);
                          }}
                          disabled={options.length === 0}
                          className={`rounded-lg border px-3 py-2 text-left text-sm transition ${
                            isActive
                              ? 'border-blue-500 bg-blue-50 text-blue-900 ring-1 ring-blue-500'
                              : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                          } disabled:cursor-not-allowed disabled:opacity-40`}
                        >
                          <span className="block font-semibold">{label}</span>
                          <span className="mt-0.5 block text-xs text-gray-500">{options.length} opção(ões)</span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Escolha a opção
                    </label>
                    <select
                      value={selectedFinancialOriginId}
                      onChange={(event) => setSelectedFinancialOriginId(event.target.value)}
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      {financialOriginGroups[selectedFinancialOriginKind].map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.title}{option.serviceType ? ` — ${option.serviceType}` : ''}
                        </option>
                      ))}
                    </select>

                    {selectedFinancialOption && (
                      <div className="mt-3 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        <div className="flex flex-wrap items-center gap-2">
                          <span>{selectedFinancialOption.subtitle}</span>
                          {selectedFinancialOption.id === financialOriginPreview.recommendedId && (
                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Recomendado</span>
                          )}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-600">
                          {selectedFinancialOption.serviceType && <span className="rounded-full bg-white px-2 py-1">Serviço: {selectedFinancialOption.serviceType}</span>}
                          {selectedFinancialOption.totalAmount != null && <span className="rounded-full bg-white px-2 py-1">Preço: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(selectedFinancialOption.totalAmount))}</span>}
                          {selectedFinancialOption.patientAmount != null && <span className="rounded-full bg-white px-2 py-1">Paciente: R$ {Number(selectedFinancialOption.patientAmount).toFixed(2)}</span>}
                          {selectedFinancialOption.requiresAuthorization && <span className="rounded-full bg-amber-100 px-2 py-1 text-amber-800">Exige autorização</span>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={handleCancelFinancialOriginSelection}
                disabled={isUpdating}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Voltar
              </button>
              <button
                onClick={handleConfirmFinancialOriginSelection}
                disabled={isUpdating || !selectedFinancialOriginId || financialOriginPreview.options.length === 0}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {isUpdating ? 'Processando...' : 'Concluir atendimento'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReopenSelectionModal && reopenPreview?.appointment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-slate-900">Selecionar Estorno para Reabertura</h3>
            </div>

            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-gray-700">
                Foram encontrados múltiplos lançamentos financeiros possíveis para este agendamento.
                Selecione qual lançamento deseja estornar antes de voltar o status para <strong>Agendado</strong>.
              </p>

              <div className="space-y-2">
                {reopenPreview.candidates.map((candidate) => {
                  const isSelected = selectedReopenCandidateId === candidate.id;
                  const amountLabel = typeof candidate.amount === 'number'
                    ? `R$ ${candidate.amount.toFixed(2)}`
                    : null;

                  return (
                    <label
                      key={candidate.id}
                      className={`block rounded-lg border p-3 cursor-pointer transition ${
                        isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="reopen-candidate"
                          value={candidate.id}
                          checked={isSelected}
                          onChange={() => setSelectedReopenCandidateId(candidate.id)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900">{candidate.title}</p>
                          {candidate.subtitle && (
                            <p className="text-sm text-gray-600 mt-0.5">{candidate.subtitle}</p>
                          )}
                          <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-500">
                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5">
                              Tipo: {candidate.kind === 'package_usage' ? 'Uso de pacote' : 'Item avulso'}
                            </span>
                            {amountLabel && (
                              <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5">
                                {amountLabel}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={handleCancelReopenSelection}
                disabled={isUpdating}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmReopenSelection}
                disabled={isUpdating || !selectedReopenCandidateId}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {isUpdating ? 'Processando...' : 'Estornar e Voltar para Agendado'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Edit Event Modal */}
      {showEditModal && editingEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold flex items-center">
                <Edit className="w-5 h-5 mr-2 text-blue-600" />
                Editar Agendamento
              </h3>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={handleCancelEdit}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="px-6 py-4 space-y-4">
              {(editFormClientId !== activeClientId || editFormTimeZone !== timeZone) && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  A clínica ou o fuso horário mudou enquanto este formulário estava aberto. Feche, reabra e revise os horários.
                </div>
              )}
              {/* Paciente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paciente *
                </label>
                <PatientSearchSelect
                  selectedPatientId={editForm.patientId}
                  onSelect={(id) => setEditForm(prev => ({ ...prev, patientId: id }))}
                  patients={patients}
                  allowInactiveSelection={false}
                />
              </div>

              {/* Título/Assunto */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título/Assunto *
                </label>
                <input
                  type="text"
                  value={editForm.subject}
                  onChange={(e) => setEditForm(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Serviço agendado *</label>
                <select
                  value={editForm.serviceId}
                  onChange={(e) => setEditForm(prev => ({ ...prev, serviceId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={loadingAgendaServices}
                >
                  <option value="">{loadingAgendaServices ? 'Carregando serviços...' : 'Selecione o serviço'}</option>
                  {agendaServices.map((service) => (
                    <option key={service.serviceId} value={service.serviceId}>
                      {service.serviceName}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">O serviço será usado para filtrar as opções financeiras.</p>
              </div>

              {/* Profissional */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profissional *
                </label>
                <select
                  value={editForm.calendar_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, calendar_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Selecione um profissional</option>
                  {selectableProfessionals.map((professional) => (
                    <option key={professional.userId} value={professional.userId}>
                      {professional.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Local */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Local *
                </label>
                {editingEvent?.locationIssue && (
                  <p className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    Agendamento legado com local inconsistente: {editingEvent.locationIssue}. Escolha um valor válido para continuar.
                  </p>
                )}
                <select
                  value={editForm.location}
                  onChange={(e) => setEditForm(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Selecione um local</option>
                  <option value="Clinica">Clínica</option>
                  <option value="Domicilio">Domicílio</option>
                  <option value="Hospitalar">Hospitalar</option>
                </select>
              </div>

              {/* Data e Hora de Início */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data e Hora de Início *
                </label>
                <input
                  type="datetime-local"
                  value={editForm.start}
                  onChange={(e) => setEditForm(prev => ({ ...prev, start: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Data e Hora de Término */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data e Hora de Término *
                </label>
                <input
                  type="datetime-local"
                  value={editForm.end}
                  onChange={(e) => setEditForm(prev => ({ ...prev, end: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <p className="text-xs text-slate-500">
                Horário local da clínica: {getTimeZoneLabel(editFormTimeZone)} ({editFormTimeZone})
              </p>

              {/* Aviso */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Atenção:</strong> As alterações serão salvas imediatamente no sistema.
                </p>
              </div>

              {/* Botões */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={editFormClientId !== activeClientId || editFormTimeZone !== timeZone}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Salvar Alterações
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-red-900">Confirmar Exclusão</h3>
            </div>

            <div className="px-6 py-4">
              <div className="flex items-center mb-4">
                <Trash2 className="w-8 h-8 text-red-600 mr-3" />
                <div>
                  <p className="text-gray-900 font-medium">Tem certeza que deseja excluir este evento?</p>
                  <p className="text-sm text-gray-600">Esta ação não pode ser desfeita.</p>
                </div>
              </div>

              {selectedEventForDeleteModal && (
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-sm text-gray-700">
                    <strong>Evento:</strong> {selectedEventForDeleteModal.title}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>Data:</strong> {moment.tz(selectedEventForDeleteModal.start, timeZone).format('DD/MM/YYYY HH:mm')}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>Profissional:</strong> {selectedEventForDeleteModal.calendar_name || 'Não informado'}
                  </p>
                </div>
              )}

              {isDeleteModalEventBlocked && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-red-800">
                    Este agendamento não pode ser excluído porque o status atual não é "Agendado".
                  </p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
                disabled={isDeleting}
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDeleteEvent(showDeleteConfirm)}
                disabled={isDeleting || isDeleteModalEventBlocked}
                className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Excluindo...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Confirmar Exclusão
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-red-900">Confirmar Exclusão em Bloco</h3>
            </div>

            <div className="px-6 py-4">
              <div className="flex items-center mb-4">
                <Trash2 className="w-8 h-8 text-red-600 mr-3" />
                <div>
                  <p className="text-gray-900 font-medium">
                    Tem certeza que deseja excluir {selectedEvents.size} evento(s)?
                  </p>
                  <p className="text-sm text-gray-600">Esta ação não pode ser desfeita.</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 mb-4 max-h-60 overflow-y-auto">
                <p className="text-sm font-medium text-gray-700 mb-2">Eventos a serem excluídos:</p>
                <ul className="space-y-2">
                  {selectedEventsList.map(event => (
                      <li key={event.id} className="text-sm text-gray-700 bg-white p-2 rounded border border-gray-200">
                        <div className="flex justify-between">
                          <span className="font-medium">{event.title}</span>
                          <span className="text-gray-500">
                            {formatInTimeZone(event.start, 'DD/MM/YYYY HH:mm', timeZone)}
                          </span>
                        </div>
                        {event.calendar_name && (
                          <div className="text-xs text-gray-500 mt-1">
                            Profissional: {event.calendar_name}
                          </div>
                        )}
                        {isProtectedAppointment(event) && (
                          <div className="text-xs text-red-600 mt-1 font-medium">
                            Bloqueado: agendamento concluído ou com prontuário/evolução.
                          </div>
                        )}
                      </li>
                    ))}
                </ul>
              </div>

              {selectedProtectedEventsCount > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-red-800">
                    Exclusão bloqueada para {selectedProtectedEventsCount} evento(s) concluído(s) ou com prontuário/evolução.
                  </p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowBulkDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
                disabled={isDeleting}
              >
                Cancelar
              </button>
              <button
                onClick={handleBulkDeleteEvents}
                disabled={isDeleting || selectedProtectedEventsCount > 0}
                className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Excluindo {selectedEvents.size} evento(s)...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Confirmar Exclusão de {selectedEvents.size} Evento(s)
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <AbsenceDecisionModal
        isOpen={Boolean(absenceDecisionPreview)}
        preview={absenceDecisionPreview}
        loading={isUpdating}
        onConfirm={async (absenceType, optionId) => {
          if (!activeClientId || !absenceDecisionAppointmentId) return;
          setIsUpdating(true);
          try {
            const result = await AppointmentStatusService.resolveAsNoShow({
              clientId: activeClientId,
              appointmentId: absenceDecisionAppointmentId,
              absenceType,
              optionId,
              idempotencyKey: absenceIdempotencyKey || crypto.randomUUID(),
            });
            await loadEvents();
            toast.success(absenceType === 'justified'
              ? 'Falta justificada registrada sem cobrança.'
              : result.financialImpact !== 'none'
                ? 'Falta registrada e efeito financeiro aplicado.'
              : result.status === 'pending'
                ? 'Falta registrada com processamento financeiro pendente.'
                : 'Falta registrada sem impacto financeiro.');
            setAbsenceDecisionPreview(null);
            setAbsenceDecisionAppointmentId('');
            setSelectedEvent(null);
          } catch (error: any) {
            toast.error(String(error?.message || 'Erro ao registrar falta.'));
          } finally {
            setIsUpdating(false);
          }
        }}
        onCancel={() => {
          setAbsenceDecisionPreview(null);
          setAbsenceDecisionAppointmentId('');
          setIsUpdating(false);
        }}
      />

      {showAttendanceCertificateModal && attendanceCertificateData && (
        <AttendanceCertificateModal
          initialData={attendanceCertificateData}
          isSubmitting={isGeneratingAttendanceCertificate}
          onClose={() => setShowAttendanceCertificateModal(false)}
          onDownload={handleDownloadAttendanceCertificate}
          onPrint={handlePrintAttendanceCertificate}
        />
      )}
    </div>
  );
};

export default AgendaPage;
